import { useMemo, useState } from "react";
import { Layout, Grid3X3, FileText, Info } from "lucide-react";

/**
 * ProjectPreview2D — pure CSS/SVG wireframe mockups built from end-user perspective.
 *
 * Three mockups represent what the END USER sees (not the business operator):
 *   - landing  : marketing/landing page that promotes the app to consumers
 *   - dashboard: the user's app HOME (account, services, sections menu, bottom nav)
 *   - detail   : a single feature/section as the user sees it
 *
 * Frontend visualElements (Plane) are used as the actual app sections / menu items.
 * Backend visualElements (Cube) are intentionally NOT shown (user never sees them).
 *
 * No LLM calls. No API costs. Works retroactively for every project.
 */

const DEFAULT_LABELS = { landing: "Landing", dashboard: "Dashboard", detail: "Detail" };
const TAB_ICONS = { landing: Layout, dashboard: Grid3X3, detail: FileText };
const DEFAULT_DISCLAIMER =
  "AI-generierte Visualisierung deiner App. Diese Vorschau dient als <strong>Inspirations-Anker</strong>, nicht als Pflicht-Vorgabe. Dein Auftragnehmer wird daraus ein finales Design entwickeln, das technisch und visuell optimal zu deinen Anforderungen passt.";

const grid = (cls = "") =>
  `bg-[image:linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] [background-size:14px_14px] ${cls}`;

function frontendOf(p) {
  return (p?.analysis?.visualElements || []).filter((e) => e.type === "Plane");
}
function pickName(el, fallback) {
  return el?.label || el?.name || el?.componentName || fallback;
}
function shortName(name, max = 22) {
  if (!name) return "";
  // Trim parenthetical "(Mobile UI)" etc. and clamp length
  const base = name.replace(/\s*\([^)]*\)\s*$/g, "").replace(/\s*[—–-]\s.*$/g, "").trim();
  return base.length > max ? base.slice(0, max - 1) + "…" : base;
}
// Deterministic "icon" for a section name (just a small geometric glyph)
function sectionGlyph(name, accent, size = 14) {
  const seed = (name || "x").charCodeAt(0) % 6;
  const inner = (
    {
      0: <circle cx="12" cy="12" r="6" fill={accent} />,
      1: <rect x="6" y="6" width="12" height="12" rx="2" fill={accent} />,
      2: <polygon points="12,5 19,18 5,18" fill={accent} />,
      3: <rect x="5" y="9" width="14" height="6" rx="3" fill={accent} />,
      4: <path d="M5 12 L12 5 L19 12 L12 19 Z" fill={accent} />,
      5: <circle cx="12" cy="12" r="6" fill="none" stroke={accent} strokeWidth="2.4" />,
    }
  )[seed];
  return (
    <svg viewBox="0 0 24 24" width={size} height={size}>
      {inner}
    </svg>
  );
}

// ---------- Browser chrome ----------
function BrowserChrome({ accent = "#00F0FF", host, children }) {
  return (
    <div
      className="rounded-lg border overflow-hidden bg-[#0A0A14] shadow-2xl"
      style={{ borderColor: `${accent}28` }}
    >
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-white/5 bg-[#060610]">
        <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
        <span className="w-2 h-2 rounded-full bg-[#FFBD2E]" />
        <span className="w-2 h-2 rounded-full bg-[#28C840]" />
        <div
          className="ml-2 flex-1 px-2 py-0.5 rounded text-[8px] font-['JetBrains_Mono'] truncate"
          style={{ background: "#0A0A14", border: `1px solid ${accent}25`, color: accent }}
        >
          {host || "your-app.com"}
        </div>
        <div className="flex gap-1">
          <span className="w-3 h-3 rounded bg-white/5" />
          <span className="w-3 h-3 rounded bg-white/5" />
        </div>
      </div>
      <div className="relative overflow-hidden">{children}</div>
    </div>
  );
}

// ============================================================
// HERO illustration: a stylised phone showing the app in-use
// ============================================================
function PhoneHero({ accent, sections, title }) {
  const safeSections = (sections.length ? sections : [
    { label: "Home" },
    { label: "Search" },
    { label: "Library" },
    { label: "Profile" },
  ]).slice(0, 4);
  return (
    <div className="relative aspect-[4/3] flex items-center justify-center overflow-hidden">
      <div className={`absolute inset-0 ${grid()}`} />
      {/* Phone frame */}
      <div
        className="relative w-[44%] aspect-[9/19] rounded-[14px] border-[3px] bg-[#05050A] overflow-hidden shadow-2xl"
        style={{ borderColor: "#1A1A22" }}
      >
        {/* notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-2 rounded-b-md bg-[#1A1A22]" />
        {/* status bar */}
        <div className="px-3 pt-2.5 pb-1 flex items-center justify-between">
          <span className="text-[6px] font-['JetBrains_Mono'] text-white/70">9:41</span>
          <div className="flex gap-0.5">
            <span className="w-2 h-1 rounded-sm bg-white/40" />
            <span className="w-2 h-1 rounded-sm bg-white/40" />
            <span className="w-2 h-1 rounded-sm bg-white/40" />
          </div>
        </div>
        {/* greeting */}
        <div className="px-3 pt-1 pb-1.5">
          <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>
            Hi Anna
          </div>
          <div className="text-[8px] font-['Outfit'] text-white truncate">{shortName(title, 16)}</div>
        </div>
        {/* hero card */}
        <div
          className="mx-2 rounded-md p-1.5 mb-1.5"
          style={{ background: `linear-gradient(135deg, ${accent}40, ${accent}10)`, border: `1px solid ${accent}40` }}
        >
          <div className="text-[5px] tracking-widest uppercase font-['JetBrains_Mono'] text-white/60">Your plan</div>
          <div className="text-white font-['JetBrains_Mono'] text-[10px] leading-none mt-0.5">Premium</div>
          <div className="flex gap-0.5 mt-1">
            <div className="h-1 flex-1 rounded" style={{ background: accent }} />
            <div className="h-1 flex-1 rounded" style={{ background: accent }} />
            <div className="h-1 flex-1 rounded" style={{ background: `${accent}40` }} />
          </div>
        </div>
        {/* tile grid */}
        <div className="px-2 grid grid-cols-2 gap-1 mb-1">
          {safeSections.slice(0, 4).map((s, i) => (
            <div
              key={i}
              className="aspect-square rounded p-1 flex flex-col justify-end"
              style={{ background: i === 0 ? `${accent}20` : "rgba(255,255,255,0.05)", border: `1px solid ${i === 0 ? accent + "40" : "rgba(255,255,255,0.06)"}` }}
            >
              <div className="self-start mb-1">{sectionGlyph(pickName(s), accent, 10)}</div>
              <div className="text-[5px] font-['Outfit'] text-white/80 truncate leading-none">
                {shortName(pickName(s), 12)}
              </div>
            </div>
          ))}
        </div>
        {/* bottom nav */}
        <div className="absolute inset-x-0 bottom-0 px-2 py-1 border-t border-white/8 bg-[#05050A]/95 flex items-center justify-around">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col items-center gap-0.5">
              <div className="w-1.5 h-1.5 rounded-sm" style={{ background: i === 0 ? accent : "rgba(255,255,255,0.25)" }} />
              <span className="text-[4px] font-['Outfit']" style={{ color: i === 0 ? accent : "rgba(255,255,255,0.35)" }}>
                {["Home", "Search", "Lib", "Me"][i]}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Floating notif bubble */}
      <div
        className="absolute right-[6%] top-[12%] w-[32%] rounded-md p-1.5 border bg-[#0A0A14]/95 shadow-2xl"
        style={{ borderColor: `${accent}50` }}
      >
        <div className="flex items-center gap-1 mb-0.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
          <span className="text-[5px] tracking-widest uppercase font-['JetBrains_Mono'] text-white/50">Now</span>
        </div>
        <div className="text-[6px] font-['Outfit'] text-white truncate">New offer for you</div>
        <div className="space-y-0.5 mt-0.5">
          <div className="h-0.5 w-3/4 rounded bg-white/15" />
          <div className="h-0.5 w-1/2 rounded bg-white/10" />
        </div>
      </div>
      {/* Floating rating bubble */}
      <div className="absolute left-[6%] bottom-[14%] rounded-md px-1.5 py-1 bg-[#0A0A14]/90 border border-white/10 flex items-center gap-1">
        <span className="text-[8px]" style={{ color: accent }}>★</span>
        <span className="text-[6px] font-['JetBrains_Mono'] text-white">4.9 · 12k</span>
      </div>
    </div>
  );
}

// ============================================================
// LANDING — consumer-facing marketing page
// ============================================================
function LandingMockup({ project, accent }) {
  const title = project?.projectTitle || "Your App";
  const cat = project?.projectCategory || "App";
  const sections = frontendOf(project);
  const featureList = (sections.length ? sections : [
    { label: "Home & Discovery" },
    { label: "Search & Filters" },
    { label: "My Library" },
    { label: "Notifications" },
    { label: "Profile & Settings" },
    { label: "Help & Support" },
  ]).slice(0, 6);
  const tags = (project?.analysis?.techStack_reco || []).slice(0, 5);

  return (
    <div className="bg-[#05050A]">
      {/* NAV */}
      <div className="px-5 py-2.5 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded" style={{ background: accent }} />
          <div className="text-[9px] font-['Outfit'] text-white tracking-tight truncate max-w-[140px]">
            {shortName(title, 22)}
          </div>
        </div>
        <div className="flex gap-3">
          {featureList.slice(0, 4).map((f, i) => (
            <span key={i} className="text-[7px] font-['Outfit'] text-white/60 truncate max-w-[80px]">
              {shortName(pickName(f), 14)}
            </span>
          ))}
          <span className="text-[7px] font-['Outfit'] text-white/60">Pricing</span>
        </div>
        <div className="flex gap-1">
          <div className="px-2 py-0.5 text-[7px] font-['Outfit'] text-white/70">Sign in</div>
          <div className="px-2 py-0.5 text-[7px] font-['Outfit'] rounded-sm font-semibold" style={{ background: accent, color: "#000" }}>
            Get the app
          </div>
        </div>
      </div>

      {/* HERO */}
      <div className="px-5 pt-6 pb-5 grid grid-cols-2 gap-5">
        <div className="flex flex-col justify-center">
          <div className="text-[7px] tracking-[0.25em] uppercase font-['JetBrains_Mono'] mb-1.5" style={{ color: accent }}>
            {cat}
          </div>
          <div className="text-white font-['Outfit'] font-light leading-[1.05] text-[18px] mb-2 line-clamp-3">
            {title}
          </div>
          <div className="space-y-1 mb-3">
            <div className="h-1.5 w-full rounded bg-white/15" />
            <div className="h-1.5 w-5/6 rounded bg-white/12" />
            <div className="h-1.5 w-2/3 rounded bg-white/10" />
          </div>
          {/* App-store CTAs */}
          <div className="flex gap-1.5 mb-2.5">
            <div className="px-2 py-1.5 rounded flex items-center gap-1.5" style={{ background: "#000", border: "1px solid rgba(255,255,255,0.2)" }}>
              <div className="w-3.5 h-3.5 rounded-sm bg-white/80" />
              <div className="leading-tight">
                <div className="text-[5px] text-white/60 font-['Outfit']">Download on</div>
                <div className="text-[7px] text-white font-['Outfit'] font-semibold">App Store</div>
              </div>
            </div>
            <div className="px-2 py-1.5 rounded flex items-center gap-1.5" style={{ background: "#000", border: "1px solid rgba(255,255,255,0.2)" }}>
              <div className="w-3.5 h-3.5 rounded-sm" style={{ background: accent }} />
              <div className="leading-tight">
                <div className="text-[5px] text-white/60 font-['Outfit']">Get it on</div>
                <div className="text-[7px] text-white font-['Outfit'] font-semibold">Google Play</div>
              </div>
            </div>
          </div>
          {/* Stats row */}
          <div className="flex gap-4">
            <div>
              <div className="text-white font-['Outfit'] text-[12px] leading-none">2.4M+</div>
              <div className="text-[6px] tracking-[0.2em] uppercase text-white/40 font-['JetBrains_Mono'] mt-0.5">Users</div>
            </div>
            <div>
              <div className="text-white font-['Outfit'] text-[12px] leading-none">4.9★</div>
              <div className="text-[6px] tracking-[0.2em] uppercase text-white/40 font-['JetBrains_Mono'] mt-0.5">Reviews</div>
            </div>
            <div>
              <div className="text-white font-['Outfit'] text-[12px] leading-none">{featureList.length}+</div>
              <div className="text-[6px] tracking-[0.2em] uppercase text-white/40 font-['JetBrains_Mono'] mt-0.5">Features</div>
            </div>
          </div>
        </div>
        <PhoneHero accent={accent} sections={sections} title={title} />
      </div>

      {/* FEATURE SHOWCASE GRID — using the real frontend sections */}
      <div className="px-5 py-4 border-t border-white/5">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>
              Was du bekommst
            </div>
            <div className="text-white font-['Outfit'] font-light text-[12px]">Alles in einer App</div>
          </div>
          <div className="text-[7px] font-['Outfit']" style={{ color: accent }}>Mehr entdecken →</div>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {featureList.slice(0, 6).map((f, i) => (
            <div key={i} className="rounded p-2.5 bg-white/[0.025] border border-white/5">
              <div
                className="w-7 h-7 rounded-md mb-2 flex items-center justify-center"
                style={{ background: `${accent}15`, border: `1px solid ${accent}40` }}
              >
                {sectionGlyph(pickName(f), accent, 14)}
              </div>
              <div className="text-[8.5px] font-['Outfit'] text-white font-medium truncate">
                {shortName(pickName(f), 24)}
              </div>
              <div className="space-y-0.5 mt-1">
                <div className="h-0.5 w-full rounded bg-white/10" />
                <div className="h-0.5 w-3/4 rounded bg-white/8" />
                <div className="h-0.5 w-1/2 rounded bg-white/6" />
              </div>
              <div className="mt-1.5 flex items-center gap-1">
                <span className="text-[6px] font-['JetBrains_Mono']" style={{ color: accent }}>Mehr →</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="px-5 py-4 border-t border-white/5 bg-white/[0.015]">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>
              So funktioniert's
            </div>
            <div className="text-white font-['Outfit'] font-light text-[11px]">In 3 Schritten startklar</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { n: "01", t: "App laden", d: "Kostenlos im Store" },
            { n: "02", t: "Konto anlegen", d: "Schnell & sicher mit SwissID" },
            { n: "03", t: "Loslegen", d: "Alle Funktionen sofort nutzbar" },
          ].map((s, i) => (
            <div key={i} className="rounded p-2 bg-[#0A0A14] border border-white/5">
              <div className="text-[14px] font-['JetBrains_Mono'] font-light leading-none" style={{ color: accent }}>
                {s.n}
              </div>
              <div className="text-[8px] font-['Outfit'] text-white mt-1.5">{s.t}</div>
              <div className="text-[6.5px] text-white/50 mt-0.5 font-['Outfit']">{s.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* TESTIMONIAL */}
      <div className="px-5 py-3.5 border-t border-white/5">
        <div className="flex items-start gap-2">
          <div className="w-6 h-6 rounded-full flex-shrink-0" style={{ background: `${accent}30`, border: `1px solid ${accent}60` }} />
          <div className="flex-1">
            <div className="flex items-center gap-1 mb-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <span key={i} className="text-[6px]" style={{ color: accent }}>★</span>
              ))}
              <span className="text-[6px] text-white/50 font-['JetBrains_Mono'] ml-0.5">5.0</span>
            </div>
            <div className="text-[7px] font-['Outfit'] text-white/85 italic">
              „Endlich alle wichtigen Services an einem Ort. Genau das, was ich gesucht habe."
            </div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mt-1">
              Anna M. · Verified user
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="px-5 py-3 border-t border-white/5 grid grid-cols-4 gap-3">
        {["App", "Hilfe", "Unternehmen", "Rechtliches"].map((c, i) => (
          <div key={i}>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1">{c}</div>
            <div className="space-y-0.5">
              {[0, 1, 2].map((j) => (
                <div key={j} className="h-0.5 rounded bg-white/8" style={{ width: `${50 + ((i + j) * 13) % 35}%` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="px-5 py-1.5 border-t border-white/5 flex items-center justify-between">
        <span className="text-[6px] text-white/30 font-['JetBrains_Mono']">© 2026 {shortName(title, 18)}</span>
        <div className="flex gap-1.5 flex-wrap">
          {tags.slice(0, 4).map((t, i) => (
            <span
              key={i}
              className="text-[6px] font-['JetBrains_Mono'] truncate max-w-[60px] px-1 py-0.5 rounded"
              style={{ background: `${accent}10`, color: accent, border: `1px solid ${accent}25` }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DASHBOARD — END USER's app HOME (account, services menu, sections, bottom nav)
// ============================================================
function DashboardMockup({ project, accent }) {
  const sections = frontendOf(project);
  const navList = (sections.length ? sections : [
    { label: "Home" },
    { label: "Services" },
    { label: "Activity" },
    { label: "Profile" },
  ]).slice(0, 5);
  const allMenu = (sections.length ? sections : [
    { label: "Discover" }, { label: "Library" }, { label: "Wallet" },
    { label: "Notifications" }, { label: "Settings" }, { label: "Support" },
  ]);

  return (
    <div className="bg-[#05050A] flex flex-col" style={{ minHeight: 460 }}>
      {/* APP HEADER */}
      <div className="px-5 pt-4 pb-3 flex items-center justify-between" style={{ background: `linear-gradient(180deg, ${accent}10, transparent)` }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center font-['Outfit'] text-[9px] font-semibold text-black" style={{ background: accent }}>
            A
          </div>
          <div>
            <div className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">Hallo</div>
            <div className="text-[10px] font-['Outfit'] text-white">Anna Müller</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-5 h-5 rounded-full bg-white/8 border border-white/10 flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
            </div>
            <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ background: accent }} />
          </div>
          <div className="w-5 h-5 rounded-full bg-white/8 border border-white/10" />
        </div>
      </div>

      {/* PRIMARY HERO CARD (user's plan/balance) */}
      <div className="px-5 pb-3">
        <div
          className="rounded-lg p-3 relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${accent}25, ${accent}05 80%)`, border: `1px solid ${accent}40` }}
        >
          <div className={`absolute inset-0 opacity-30 ${grid()}`} />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono'] text-white/60">Dein Paket</div>
              <div className="text-white font-['Outfit'] font-light text-[15px] leading-tight mt-0.5">Premium Plus</div>
              <div className="text-[7px] text-white/50 mt-0.5 font-['Outfit']">Aktiv · verlängert sich am 12.06.2026</div>
            </div>
            <div className="text-right">
              <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono'] text-white/60">Datenvolumen</div>
              <div className="text-white font-['JetBrains_Mono'] text-[12px] leading-none mt-0.5">28.4 / 50 GB</div>
            </div>
          </div>
          {/* progress */}
          <div className="relative mt-2.5 h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: "57%", background: accent }} />
          </div>
          <div className="relative flex gap-1.5 mt-2.5">
            <div className="px-2 py-1 rounded text-[7px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>
              Verwalten →
            </div>
            <div className="px-2 py-1 rounded text-[7px] font-['Outfit'] bg-white/8 text-white/80 border border-white/10">
              Upgrade
            </div>
          </div>
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="px-5 pb-3 grid grid-cols-4 gap-2">
        {[
          { l: "Bezahlen", g: 0 },
          { l: "Aufladen", g: 1 },
          { l: "Scannen", g: 2 },
          { l: "Senden", g: 3 },
        ].map((a, i) => (
          <div key={i} className="rounded p-2 bg-white/[0.04] border border-white/8 flex flex-col items-center gap-1">
            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}>
              {sectionGlyph("a" + i, accent, 12)}
            </div>
            <span className="text-[7px] font-['Outfit'] text-white/85 text-center leading-tight">{a.l}</span>
          </div>
        ))}
      </div>

      {/* SECTIONS MENU — uses real frontend visualElements */}
      <div className="px-5 pb-3">
        <div className="flex items-end justify-between mb-2">
          <div className="text-[7px] font-['Outfit'] text-white/80 font-medium">Deine Bereiche</div>
          <div className="text-[6px] font-['JetBrains_Mono']" style={{ color: accent }}>Alle anzeigen →</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {allMenu.slice(0, 6).map((m, i) => (
            <div
              key={i}
              className="rounded-md p-2 flex items-center gap-2 border"
              style={{
                background: i === 0 ? `${accent}12` : "rgba(255,255,255,0.025)",
                borderColor: i === 0 ? `${accent}40` : "rgba(255,255,255,0.06)",
              }}
            >
              <div
                className="w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center"
                style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}
              >
                {sectionGlyph(pickName(m), accent, 16)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[8px] font-['Outfit'] text-white truncate font-medium">
                  {shortName(pickName(m), 26)}
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[5.5px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>
                    Öffnen
                  </span>
                  <span className="text-[5.5px] tracking-widest uppercase text-white/30 font-['JetBrains_Mono']">
                    {i === 0 ? "· 3 NEU" : "· bereit"}
                  </span>
                </div>
              </div>
              {i === 0 && (
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* PROMO BANNER */}
      <div className="px-5 pb-3">
        <div
          className="rounded-md p-2.5 flex items-center gap-2"
          style={{ background: `linear-gradient(90deg, ${accent}20, ${accent}05)`, border: `1px solid ${accent}30` }}
        >
          <div className="flex-1">
            <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>
              Empfehlung
            </div>
            <div className="text-[8px] font-['Outfit'] text-white mt-0.5">Spare 25% mit dem Family-Plan</div>
          </div>
          <div className="px-2 py-1 rounded text-[7px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>
            Mehr →
          </div>
        </div>
      </div>

      {/* RECENT ACTIVITY */}
      <div className="px-5 pb-4 flex-1">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[7px] font-['Outfit'] text-white/80 font-medium">Letzte Aktivität</div>
          <div className="text-[6px] font-['JetBrains_Mono'] text-white/40">Diese Woche</div>
        </div>
        <div className="space-y-1.5">
          {[
            { t: "Rechnung bezahlt", d: "Heute · 14:32", v: "− 89.50 CHF" },
            { t: "Datenpaket aktiviert", d: "Gestern · 09:12", v: "+ 5 GB" },
            { t: "Belohnung erhalten", d: "Mo · 17:45", v: "+ 50 Pkt" },
          ].map((a, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/[0.025] border border-white/5">
              <div className="w-5 h-5 rounded-full flex-shrink-0" style={{ background: `${accent}${["20", "15", "10"][i]}`, border: `1px solid ${accent}30` }} />
              <div className="flex-1 min-w-0">
                <div className="text-[7px] font-['Outfit'] text-white truncate">{a.t}</div>
                <div className="text-[5.5px] text-white/40 font-['JetBrains_Mono']">{a.d}</div>
              </div>
              <div className="text-[7px] font-['JetBrains_Mono']" style={{ color: i === 0 ? "#FF6F6F" : accent }}>
                {a.v}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* BOTTOM NAV (using real frontend section names) */}
      <div className="px-2 py-2 border-t border-white/8 bg-[#05050A]/95 flex items-center justify-around sticky bottom-0">
        {navList.slice(0, 5).map((n, i) => (
          <div key={i} className="flex flex-col items-center gap-0.5">
            <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: i === 0 ? `${accent}20` : "transparent" }}>
              {sectionGlyph(pickName(n), i === 0 ? accent : "#909098", 12)}
            </div>
            <span className="text-[6px] font-['Outfit'] truncate max-w-[60px]" style={{ color: i === 0 ? accent : "rgba(255,255,255,0.45)" }}>
              {shortName(pickName(n), 10)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// DETAIL — END USER's view of a single section/feature
// ============================================================
// ============================================================
// DETAIL DISPATCHER — picks the right detail layout per category
// ============================================================
function detectVertical(project) {
  const cat = (project?.projectCategory || "").toLowerCase().trim();
  const title = (project?.projectTitle || "").toLowerCase();
  const desc = (project?.projectDescription || "").toLowerCase();
  const blob = `${title} ${desc}`;

  // 1) Enterprise categories (new) — match by category first, then keywords
  if (cat.includes("cloud") || cat.includes("migration") || cat.includes("infrastruktur")) return "cloud";
  if (cat.includes("data") || cat.includes("ai hub") || cat.includes("analytics") || cat.includes("ml")) return "data";
  if (cat.includes("security") || cat.includes("compliance") || cat.includes("sicherheit") || cat.includes("audit")) return "security";
  if (cat.includes("integration") || cat.includes("enterprise integration") || cat.includes("ipaas")) return "integration";

  // 2) Mobile App — needs sub-detection (was previously always "Chat")
  const isMobile = cat.includes("mobile app") || cat === "ios" || cat === "android";
  if (isMobile) {
    if (/\b(fitness|workout|training|run|laufen|sport|gym|health.*track|wellness)\b/.test(blob)) return "mobile-fitness";
    if (/\b(social|feed|post|follow|community|stories|reels)\b/.test(blob)) return "mobile-social";
    if (/\b(book|booking|termin|appointment|reservier|schedule|kalender|slot)\b/.test(blob)) return "mobile-booking";
    if (/\b(chat|messag|messenger|conversation|dm)\b/.test(blob)) return "mobile-chat";
    return "mobile-profile"; // sane default for generic mobile apps
  }

  // 3) Other industry verticals
  if (cat.includes("fintech") || cat.includes("bank") || cat.includes("finanz") || cat.includes("payment")) return "fintech";
  if (cat.includes("health") || cat.includes("medi") || cat.includes("hospital") || cat.includes("clinic") || cat.includes("gesundheit")) return "health";
  if (cat.includes("commerce") || cat.includes("retail") || cat.includes("shop")) return "ecommerce";
  if (cat.includes("saas") || cat.includes("crm") || cat.includes("erp") || cat.includes("dashboard") || cat.includes("admin")) return "saas";

  // 4) Description-based fallback
  if (/\b(kubernetes|k8s|aws|azure|gcp|terraform|cloud.*migration|legacy.*cloud)\b/.test(blob)) return "cloud";
  if (/\b(data.*warehouse|etl|pipeline|machine learning|llm|ai agent|analytics|dashboards?)\b/.test(blob)) return "data";
  if (/\b(iso 27001|gdpr|dsgvo|pentest|vulnerab|siem|iam|zero.trust|incident)\b/.test(blob)) return "security";
  if (/\b(sap|salesforce|ipaas|workflow|orchestrat|connector|api gateway)\b/.test(blob)) return "integration";
  if (/\b(fintech|bank|payment|wallet|invest|trading|finanz|finma|portfolio)\b/.test(blob)) return "fintech";
  if (/\b(health|medi|patient|clinic|doctor|gesundheit|arzt|hospital|hipaa|mdr)\b/.test(blob)) return "health";
  if (/\b(commerce|retail|shop|store|cart|checkout|b2c)\b/.test(blob)) return "ecommerce";
  if (/\b(saas|admin|crm|erp|workspace|productivity)\b/.test(blob)) return "saas";
  return "default";
}

// ============================================================
// DETAIL · E-COMMERCE — Product detail page
// ============================================================
function DetailEcommerce({ project, accent }) {
  const sections = frontendOf(project);
  const productName = pickName(sections[0], "Premium Item");
  const tags = (project?.analysis?.techStack_reco || []).slice(0, 4);
  return (
    <div className="bg-[#05050A]">
      {/* Top bar */}
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-white/8 flex items-center justify-center">
          <span className="text-[7px] text-white/60">←</span>
        </div>
        <div className="flex-1 truncate text-[8px] font-['Outfit'] text-white/70">{shortName(productName, 36)}</div>
        <div className="w-5 h-5 rounded-full bg-white/8 border border-white/10 flex items-center justify-center"><span className="text-[8px] text-white/60">♡</span></div>
        <div className="w-5 h-5 rounded-full bg-white/8 border border-white/10 flex items-center justify-center"><span className="text-[7px] text-white/60">⤴</span></div>
      </div>

      <div className="px-4 py-3 grid grid-cols-5 gap-3">
        {/* Left: gallery */}
        <div className="col-span-2 space-y-2">
          {/* Main image */}
          <div className="relative aspect-square rounded-md overflow-hidden border" style={{ borderColor: `${accent}30`, background: `linear-gradient(135deg, ${accent}25, ${accent}05)` }}>
            <div className={`absolute inset-0 ${grid()}`} />
            <div className="absolute inset-0 flex items-center justify-center">{sectionGlyph(productName, accent, 56)}</div>
            <div className="absolute top-1.5 left-1.5 px-1 py-0.5 rounded text-[6px] font-['JetBrains_Mono'] font-bold" style={{ background: "#FF3B30", color: "#fff" }}>−25%</div>
            <div className="absolute bottom-1.5 right-1.5 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center"><span className="text-[8px] text-white">⌕</span></div>
          </div>
          {/* Thumbnails */}
          <div className="grid grid-cols-4 gap-1">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="aspect-square rounded border" style={{ background: i === 0 ? `${accent}20` : `${accent}08`, borderColor: i === 0 ? accent : "rgba(255,255,255,0.08)" }} />
            ))}
          </div>
        </div>

        {/* Right: title, price, options, CTAs */}
        <div className="col-span-3 space-y-2">
          <div>
            <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Bestseller</div>
            <div className="text-white font-['Outfit'] font-light text-[14px] leading-tight mt-0.5">{shortName(productName, 36)}</div>
            <div className="flex items-center gap-1.5 mt-1">
              <div className="flex">{[0, 1, 2, 3, 4].map((i) => <span key={i} className="text-[7px]" style={{ color: i < 4 ? accent : "rgba(255,255,255,0.2)" }}>★</span>)}</div>
              <span className="text-[6.5px] font-['JetBrains_Mono'] text-white/50">4.8 · 2.4k</span>
              <span className="text-[6px] text-white/30">·</span>
              <span className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: accent }}>auf Lager</span>
            </div>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-2">
            <span className="text-white font-['Outfit'] font-light text-[18px] leading-none">CHF 89.00</span>
            <span className="text-[8px] text-white/40 font-['JetBrains_Mono'] line-through">119.00</span>
            <span className="text-[7px] font-['JetBrains_Mono'] px-1 py-0.5 rounded" style={{ background: `${accent}20`, color: accent }}>SPAREN 25%</span>
          </div>

          {/* Color picker */}
          <div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1">Farbe · Cyan</div>
            <div className="flex gap-1">
              {[accent, "#FF6F6F", "#FFD166", "#9D8DF1", "#fff"].map((c, i) => (
                <div key={i} className="w-5 h-5 rounded-full border-2" style={{ background: c, borderColor: i === 0 ? "#fff" : "transparent" }} />
              ))}
            </div>
          </div>

          {/* Size picker */}
          <div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1">Grösse</div>
            <div className="flex gap-1">
              {["S", "M", "L", "XL"].map((s, i) => (
                <div key={s} className="w-7 h-7 rounded flex items-center justify-center text-[8px] font-['Outfit'] border" style={{ background: i === 1 ? `${accent}20` : "transparent", borderColor: i === 1 ? accent : "rgba(255,255,255,0.12)", color: i === 1 ? accent : "rgba(255,255,255,0.6)" }}>{s}</div>
              ))}
            </div>
          </div>

          {/* Quantity + Add */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/10 bg-white/[0.03]">
              <span className="text-[10px] text-white/60">−</span>
              <span className="text-[8px] font-['JetBrains_Mono'] text-white">1</span>
              <span className="text-[10px] text-white/60">+</span>
            </div>
            <div className="flex-1 px-2 py-1.5 rounded text-center text-[8px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>In den Warenkorb · CHF 89.00</div>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-1 pt-1.5 border-t border-white/5">
            {[
              { l: "Versand", v: "Gratis" },
              { l: "Rückgabe", v: "30 Tage" },
              { l: "Lieferung", v: "1-2 T." },
            ].map((b, i) => (
              <div key={i} className="text-center">
                <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">{b.l}</div>
                <div className="text-[7px] font-['Outfit'] text-white mt-0.5">{b.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Reviews preview */}
      <div className="px-4 pb-3">
        <div className="rounded-md p-2.5 bg-white/[0.025] border border-white/5">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[7px] font-['Outfit'] text-white">Bewertungen (2'437)</div>
            <div className="text-[6px] font-['JetBrains_Mono']" style={{ color: accent }}>Alle anzeigen →</div>
          </div>
          {[
            { n: "Sarah M.", t: "Top Qualität, schnelle Lieferung. Würde wieder kaufen.", s: 5 },
            { n: "Michael K.", t: "Wirklich praktisch im Alltag — empfehlenswert.", s: 4 },
          ].map((r, i) => (
            <div key={i} className="flex items-start gap-1.5 py-1 border-t border-white/5 first:border-t-0">
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: `${accent}40` }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="text-[7px] font-['Outfit'] text-white font-medium">{r.n}</span>
                  <div className="flex">{[0, 1, 2, 3, 4].map((j) => <span key={j} className="text-[5px]" style={{ color: j < r.s ? accent : "rgba(255,255,255,0.2)" }}>★</span>)}</div>
                </div>
                <div className="text-[6.5px] font-['Outfit'] text-white/70 mt-0.5 line-clamp-1">"{r.t}"</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="px-4 pb-3 flex gap-1 flex-wrap">
        {tags.map((tg, i) => (
          <span key={i} className="px-1.5 py-0.5 rounded text-[6.5px] font-['JetBrains_Mono'] truncate max-w-[100px]" style={{ background: `${accent}10`, color: accent, border: `1px solid ${accent}25` }}>{tg}</span>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · FINTECH — Account / Transaction history
// ============================================================
function DetailFintech({ project, accent }) {
  const accountName = pickName(frontendOf(project)[0], project?.projectTitle || "Konto");
  const txs = [
    { who: "Migros", cat: "Einkauf", t: "Heute · 18:24", v: "−47.80", neg: true, g: 0 },
    { who: "Anna Müller", cat: "Überweisung", t: "Heute · 09:12", v: "+250.00", neg: false, g: 1 },
    { who: "SBB", cat: "Verkehr", t: "Gestern · 17:45", v: "−9.40", neg: true, g: 2 },
    { who: "Spotify", cat: "Abo", t: "Mo · 06:00", v: "−14.95", neg: true, g: 3 },
    { who: "Lohn März", cat: "Gehalt", t: "01.03 · 09:00", v: "+5'820.00", neg: false, g: 4 },
  ];

  return (
    <div className="bg-[#05050A]">
      {/* Top bar */}
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-white/8 flex items-center justify-center"><span className="text-[7px] text-white/60">←</span></div>
        <div className="flex-1 text-center text-[8px] font-['Outfit'] text-white/70">{shortName(accountName, 26)}</div>
        <div className="w-5 h-5 rounded-full bg-white/8 border border-white/10 flex items-center justify-center"><span className="text-[8px] text-white/60">⋯</span></div>
      </div>

      {/* Balance hero */}
      <div className="px-4 pt-4 pb-3" style={{ background: `linear-gradient(180deg, ${accent}15, transparent 80%)` }}>
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono'] text-white/50">Verfügbar</div>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-white font-['Outfit'] font-light text-[24px] leading-none">CHF 12'847</span>
          <span className="text-white/50 font-['JetBrains_Mono'] text-[12px]">.50</span>
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[6.5px] font-['JetBrains_Mono'] px-1 py-0.5 rounded" style={{ background: `${accent}20`, color: accent }}>+ CHF 421.20 diesen Monat</span>
          <span className="text-[6px] text-white/40 font-['JetBrains_Mono']">IBAN CH93 0076 ····</span>
        </div>

        {/* Sparkline */}
        <div className="mt-2 h-10">
          <svg viewBox="0 0 200 40" preserveAspectRatio="none" className="w-full h-full">
            <defs>
              <linearGradient id={`fg-${accent.replace("#", "")}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={accent} stopOpacity="0.4" />
                <stop offset="100%" stopColor={accent} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M0 30 L20 25 L40 28 L60 18 L80 22 L100 12 L120 15 L140 8 L160 10 L180 6 L200 4 L200 40 L0 40 Z" fill={`url(#fg-${accent.replace("#", "")})`} />
            <path d="M0 30 L20 25 L40 28 L60 18 L80 22 L100 12 L120 15 L140 8 L160 10 L180 6 L200 4" fill="none" stroke={accent} strokeWidth="1.5" />
          </svg>
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-4 grid grid-cols-4 gap-1.5 pb-3">
        {[
          { l: "Senden", icon: "↗" },
          { l: "Anfordern", icon: "↙" },
          { l: "Aufladen", icon: "+" },
          { l: "Scannen", icon: "▢" },
        ].map((a, i) => (
          <div key={i} className="rounded p-1.5 flex flex-col items-center gap-1 bg-white/[0.04] border border-white/8">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: `${accent}20`, border: `1px solid ${accent}40` }}>
              <span className="text-[10px]" style={{ color: accent }}>{a.icon}</span>
            </div>
            <span className="text-[7px] font-['Outfit'] text-white">{a.l}</span>
          </div>
        ))}
      </div>

      {/* Transactions list */}
      <div className="px-4 pb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[7.5px] font-['Outfit'] text-white font-medium">Transaktionen</div>
          <div className="flex gap-1 items-center">
            <span className="text-[6px] font-['JetBrains_Mono'] px-1 py-0.5 rounded" style={{ background: `${accent}15`, color: accent }}>März 2026</span>
            <span className="text-[6px] font-['JetBrains_Mono'] text-white/40">Filter</span>
          </div>
        </div>
        <div className="rounded bg-white/[0.025] border border-white/5">
          {txs.map((tx, i) => (
            <div key={i} className="px-2.5 py-2 flex items-center gap-2 border-t border-white/5 first:border-t-0">
              <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: tx.neg ? "rgba(255,111,111,0.12)" : `${accent}20`, border: `1px solid ${tx.neg ? "rgba(255,111,111,0.3)" : accent + "40"}` }}>
                <span className="text-[9px]" style={{ color: tx.neg ? "#FF6F6F" : accent }}>{tx.neg ? "↓" : "↑"}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[8px] font-['Outfit'] text-white truncate font-medium">{tx.who}</div>
                <div className="text-[6.5px] text-white/40 font-['JetBrains_Mono']">{tx.cat} · {tx.t}</div>
              </div>
              <div className="text-right">
                <div className="text-[9px] font-['JetBrains_Mono']" style={{ color: tx.neg ? "#FF6F6F" : accent }}>
                  {tx.v} <span className="text-[6.5px] text-white/40">CHF</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · SAAS — Settings panel
// ============================================================
function DetailSaas({ project, accent }) {
  const sections = frontendOf(project);
  const navItems = ["Konto", "Benachrichtigungen", "Privatsphäre", "Abrechnung", "Team", "Integrationen"];
  const subSection = pickName(sections[0], "Account-Einstellungen");

  return (
    <div className="bg-[#05050A] flex" style={{ minHeight: 460 }}>
      {/* Sidebar */}
      <div className="w-[140px] border-r border-white/5 p-3">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-2">Einstellungen</div>
        <div className="space-y-0.5">
          {navItems.map((n, i) => (
            <div key={n} className="flex items-center gap-1.5 px-1.5 py-1.5 rounded" style={{ background: i === 1 ? `${accent}15` : "transparent" }}>
              <div className="w-1 h-3 rounded-full" style={{ background: i === 1 ? accent : "transparent" }} />
              <span className="text-[7.5px] font-['Outfit']" style={{ color: i === 1 ? accent : "rgba(255,255,255,0.6)" }}>{n}</span>
            </div>
          ))}
        </div>
        {/* Sidebar footer */}
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="rounded p-2 border border-white/5 bg-white/[0.02]">
            <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Plan</div>
            <div className="text-[8px] font-['Outfit'] text-white">Pro</div>
            <div className="h-1 rounded bg-white/10 mt-1 overflow-hidden">
              <div className="h-full rounded" style={{ width: "70%", background: accent }} />
            </div>
            <div className="text-[6px] text-white/40 mt-1 font-['JetBrains_Mono']">7 / 10 Plätze</div>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3 pb-3 border-b border-white/5">
          <div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Bereich</div>
            <div className="text-white font-['Outfit'] font-light text-[14px] leading-tight">{shortName(subSection, 30)}</div>
            <div className="text-[7px] text-white/50 mt-0.5 font-['Outfit']">Verwalte deine Mail-, Push- und SMS-Einstellungen</div>
          </div>
          <div className="text-[6px] font-['JetBrains_Mono'] px-1.5 py-0.5 rounded" style={{ background: `${accent}15`, color: accent }}>● Synchronisiert</div>
        </div>

        {/* Toggle list */}
        <div className="space-y-2 mb-4">
          {[
            { l: "E-Mail-Benachrichtigungen", d: "Wöchentliche Zusammenfassung & wichtige Updates", on: true },
            { l: "Push auf Mobile", d: "Echtzeit-Hinweise auf deinem iPhone/Android", on: true },
            { l: "Marketing-E-Mails", d: "Produkt-Tipps, Webinare und neue Features", on: false },
            { l: "Wöchentlicher Report", d: "Aktivitäts-Übersicht jeden Montag um 09:00", on: true },
            { l: "Geräte-Anmeldungen", d: "Sicherheits-Warnung bei neuem Login", on: true },
          ].map((t, i) => (
            <div key={i} className="flex items-start justify-between gap-2 py-2 border-t border-white/5 first:border-t-0">
              <div className="flex-1 min-w-0">
                <div className="text-[8px] font-['Outfit'] text-white font-medium">{t.l}</div>
                <div className="text-[6.5px] text-white/50 mt-0.5 font-['Outfit']">{t.d}</div>
              </div>
              <div className="w-8 h-4 rounded-full relative flex-shrink-0 transition" style={{ background: t.on ? accent : "rgba(255,255,255,0.12)" }}>
                <div className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all" style={{ left: t.on ? "16px" : "2px" }} />
              </div>
            </div>
          ))}
        </div>

        {/* Form fields */}
        <div className="rounded-md p-3 bg-white/[0.025] border border-white/5 mb-3">
          <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-2">Profil-Info</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { l: "Anzeige-Name", v: "Anna Müller" },
              { l: "E-Mail", v: "anna.mueller@example.ch" },
            ].map((f, i) => (
              <div key={i}>
                <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1">{f.l}</div>
                <div className="px-2 py-1.5 rounded bg-[#0A0A14] border border-white/8 text-[7.5px] font-['Outfit'] text-white truncate">{f.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Save bar */}
        <div className="flex items-center justify-between pt-3 border-t border-white/5">
          <div className="text-[6.5px] text-white/50 font-['JetBrains_Mono']">Letzte Änderung · vor 3 Min</div>
          <div className="flex gap-1.5">
            <div className="px-3 py-1.5 rounded text-[7.5px] font-['Outfit'] bg-white/8 text-white/80 border border-white/10">Abbrechen</div>
            <div className="px-3 py-1.5 rounded text-[7.5px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Änderungen speichern</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · MOBILE — Chat / messaging thread
// ============================================================
function DetailMobile({ project, accent }) {
  const sections = frontendOf(project);
  const subject = pickName(sections[0], project?.projectTitle || "Chat");

  const messages = [
    { from: "them", t: "Hi! Hast du das Update schon gesehen?", time: "14:22" },
    { from: "me", t: "Ja, sieht super aus 🚀", time: "14:23" },
    { from: "them", t: "Wann hast du Zeit, das durchzugehen?", time: "14:23" },
    { from: "me", t: "Heute Nachmittag passt — vielleicht 16:00?", time: "14:24", read: true },
    { from: "them", t: "Perfekt, ich schicke dir den Link.", time: "14:24" },
    { from: "them", t: "📎 Zoom-Meeting · 16:00–17:00", time: "14:25", attach: true },
  ];

  return (
    <div className="bg-[#05050A] flex flex-col" style={{ minHeight: 480 }}>
      {/* Chat header */}
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2" style={{ background: `linear-gradient(180deg, ${accent}10, transparent)` }}>
        <span className="text-[10px] text-white/60">←</span>
        <div className="w-7 h-7 rounded-full flex items-center justify-center font-['Outfit'] text-[9px] font-semibold text-black" style={{ background: accent }}>M</div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-['Outfit'] text-white truncate">Markus Weber</div>
          <div className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full" style={{ background: accent }} />
            <span className="text-[6.5px] text-white/50 font-['JetBrains_Mono']">Online · {shortName(subject, 18)}</span>
          </div>
        </div>
        <span className="text-[10px] text-white/60">📞</span>
        <span className="text-[10px] text-white/60">⋮</span>
      </div>

      {/* Date separator */}
      <div className="text-center py-2">
        <span className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono'] text-white/40 px-2 py-0.5 rounded-full bg-white/5">Heute</span>
      </div>

      {/* Messages */}
      <div className="flex-1 px-4 space-y-1.5 overflow-hidden">
        {messages.map((m, i) => {
          const me = m.from === "me";
          return (
            <div key={i} className={`flex ${me ? "justify-end" : "justify-start"}`}>
              <div className="flex flex-col" style={{ maxWidth: "70%" }}>
                <div
                  className="px-2.5 py-1.5 rounded-2xl text-[8px] font-['Outfit'] leading-snug"
                  style={
                    me
                      ? { background: accent, color: "#000", borderBottomRightRadius: "4px" }
                      : { background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.05)", borderBottomLeftRadius: "4px" }
                  }
                >
                  {m.t}
                </div>
                {m.attach && (
                  <div className="mt-1 px-2 py-1.5 rounded-md border flex items-center gap-1.5 self-start" style={{ background: "rgba(255,255,255,0.04)", borderColor: `${accent}30` }}>
                    <div className="w-5 h-5 rounded" style={{ background: `${accent}20`, border: `1px solid ${accent}50` }} />
                    <div>
                      <div className="text-[7px] font-['Outfit'] text-white">Zoom-Meeting</div>
                      <div className="text-[5.5px] text-white/40 font-['JetBrains_Mono']">16:00–17:00 · zoom.us</div>
                    </div>
                  </div>
                )}
                <div className={`text-[5.5px] font-['JetBrains_Mono'] text-white/30 mt-0.5 ${me ? "text-right" : "text-left"}`}>
                  {m.time} {me && m.read && <span style={{ color: accent }}>✓✓</span>}
                </div>
              </div>
            </div>
          );
        })}
        {/* typing */}
        <div className="flex items-center gap-1 mt-2">
          <div className="w-5 h-5 rounded-full" style={{ background: `${accent}40` }} />
          <div className="px-2.5 py-1.5 rounded-2xl bg-white/[0.06] border border-white/5 flex gap-0.5" style={{ borderBottomLeftRadius: "4px" }}>
            <span className="w-1 h-1 rounded-full bg-white/50" />
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span className="w-1 h-1 rounded-full bg-white/30" />
          </div>
        </div>
      </div>

      {/* Input bar */}
      <div className="px-3 py-2 border-t border-white/5 flex items-center gap-1.5 bg-[#05050A]">
        <span className="text-[12px] text-white/50">+</span>
        <div className="flex-1 px-2 py-1.5 rounded-full bg-white/[0.04] border border-white/8 flex items-center gap-1">
          <span className="text-[7px] text-white/40 font-['Outfit']">Nachricht schreiben…</span>
          <span className="ml-auto text-[10px] text-white/40">😊</span>
        </div>
        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: accent }}>
          <span className="text-[10px] text-black font-bold">↑</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · HEALTH — Appointment detail
// ============================================================
function DetailHealth({ project, accent }) {
  const subject = pickName(frontendOf(project)[0], "Termin-Übersicht");
  return (
    <div className="bg-[#05050A]">
      {/* Top bar */}
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <span className="text-[8px] text-white/60">←</span>
        <div className="flex-1 truncate text-[8px] font-['Outfit'] text-white/70">{shortName(subject, 30)}</div>
        <div className="px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}>BESTÄTIGT</div>
      </div>

      {/* Hero card with date */}
      <div className="px-4 pt-4 pb-3">
        <div className="rounded-md p-3 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}25, ${accent}05)`, border: `1px solid ${accent}40` }}>
          <div className={`absolute inset-0 opacity-30 ${grid()}`} />
          <div className="relative grid grid-cols-3 gap-3 items-center">
            {/* Big date */}
            <div className="text-center rounded p-2" style={{ background: "rgba(0,0,0,0.4)", border: `1px solid ${accent}30` }}>
              <div className="text-[6px] tracking-widest uppercase text-white/60 font-['JetBrains_Mono']">März</div>
              <div className="text-white font-['Outfit'] font-light text-[28px] leading-none mt-0.5">14</div>
              <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Montag</div>
            </div>
            <div className="col-span-2">
              <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Vorsorge-Untersuchung</div>
              <div className="text-white font-['Outfit'] font-light text-[14px] leading-tight mt-0.5">Routinekontrolle</div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="text-[8px] text-white">⏱</span>
                <span className="text-[7.5px] font-['JetBrains_Mono'] text-white/80">10:30 – 11:15 Uhr</span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[8px] text-white">📍</span>
                <span className="text-[7.5px] font-['Outfit'] text-white/80 truncate">Praxis Dr. Schneider · Zürich</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Doctor card */}
      <div className="px-4 pb-3">
        <div className="rounded-md p-2.5 bg-white/[0.025] border border-white/5 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full flex-shrink-0" style={{ background: `linear-gradient(135deg, ${accent}40, ${accent}10)`, border: `1px solid ${accent}50` }} />
          <div className="flex-1 min-w-0">
            <div className="text-[8px] font-['Outfit'] text-white font-medium">Dr. med. Laura Schneider</div>
            <div className="text-[6.5px] text-white/50 font-['JetBrains_Mono']">Allgemeine Innere Medizin · FMH</div>
            <div className="flex items-center gap-1 mt-0.5">
              <div className="flex">{[0, 1, 2, 3, 4].map((i) => <span key={i} className="text-[6px]" style={{ color: i < 5 ? accent : "rgba(255,255,255,0.2)" }}>★</span>)}</div>
              <span className="text-[6px] font-['JetBrains_Mono'] text-white/40">4.9 · 312 Bewertungen</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="px-2 py-0.5 rounded text-[6.5px] font-['Outfit'] bg-white/8 text-white/80 border border-white/10">📞 Anrufen</div>
            <div className="px-2 py-0.5 rounded text-[6.5px] font-['Outfit']" style={{ background: `${accent}15`, color: accent, border: `1px solid ${accent}30` }}>💬 Chat</div>
          </div>
        </div>
      </div>

      {/* Preparation checklist */}
      <div className="px-4 pb-3">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1.5">Vorbereitung (3/4)</div>
        <div className="rounded-md bg-white/[0.025] border border-white/5">
          {[
            { l: "Versicherungskarte mitbringen", done: true },
            { l: "Nüchtern erscheinen (8 Std.)", done: true },
            { l: "Bisherige Medikamente notieren", done: true },
            { l: "Online-Anamnese ausfüllen", done: false, action: "Jetzt ausfüllen" },
          ].map((it, i) => (
            <div key={i} className="px-2.5 py-1.5 flex items-center gap-2 border-t border-white/5 first:border-t-0">
              <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: it.done ? `${accent}30` : "transparent", border: `1.5px solid ${it.done ? accent : "rgba(255,255,255,0.2)"}` }}>
                {it.done && (
                  <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke={accent} strokeWidth="3"><path d="M5 13 l4 4 L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                )}
              </div>
              <div className="flex-1 text-[7.5px] font-['Outfit']" style={{ color: it.done ? "rgba(255,255,255,0.5)" : "#fff", textDecoration: it.done ? "line-through" : "none" }}>{it.l}</div>
              {it.action && (
                <div className="px-1.5 py-0.5 rounded text-[6px] font-['Outfit']" style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}>{it.action} →</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTAs */}
      <div className="px-4 py-2.5 border-t border-white/8 flex gap-1.5">
        <div className="flex-1 px-3 py-1.5 rounded text-center text-[7.5px] font-['Outfit'] bg-white/8 text-white/80 border border-white/10">Termin verschieben</div>
        <div className="px-3 py-1.5 rounded text-[7.5px] font-['Outfit'] text-white/70 border border-white/10" style={{ background: "rgba(255,111,111,0.1)", color: "#FF6F6F", borderColor: "rgba(255,111,111,0.3)" }}>Absagen</div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · MOBILE-PROFILE — Settings/Profile (new mobile default, replaces Chat)
// ============================================================
function DetailProfile({ project, accent }) {
  const sections = frontendOf(project);
  const appName = pickName(sections[0], project?.projectTitle || "App");
  const items = sections.slice(0, 5).map((s, i) => pickName(s, `Bereich ${i + 1}`));
  const menuFallback = ["Konto & Profil", "Benachrichtigungen", "Sicherheit", "Zahlungen", "Hilfe & Support"];
  const menu = items.length >= 3 ? items : menuFallback;

  return (
    <div className="bg-[#05050A]" style={{ minHeight: 480 }}>
      {/* Top bar */}
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <span className="text-[10px] text-white/60">←</span>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white text-center">Mein Profil</div>
        <span className="text-[10px] text-white/60">⚙</span>
      </div>

      {/* Hero with avatar */}
      <div className="relative px-4 pt-5 pb-4" style={{ background: `linear-gradient(180deg, ${accent}18, transparent)` }}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-14 h-14 rounded-full flex items-center justify-center font-['Outfit'] text-[14px] font-light text-black" style={{ background: accent }}>SK</div>
            <div className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-[#05050A]" style={{ background: "#28C840" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-['Outfit'] font-light text-[13px] leading-tight">Sarah Keller</div>
            <div className="text-[6.5px] font-['JetBrains_Mono'] text-white/40 mt-0.5">sarah.keller@mail.ch · Premium</div>
            <div className="flex items-center gap-1 mt-1">
              <span className="px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: `${accent}20`, color: accent }}>VERIFIZIERT</span>
              <span className="px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: "rgba(255,184,0,0.15)", color: "#FFB800" }}>LEVEL 4</span>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { l: "Aktivitäten", v: "247" },
            { l: "Folge ich", v: "1'284" },
            { l: "Punkte", v: "5'820" },
          ].map((s, i) => (
            <div key={i} className="text-center py-1.5 rounded-md bg-white/[0.04] border border-white/8">
              <div className="text-white font-['Outfit'] text-[11px] font-light leading-none">{s.v}</div>
              <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Settings list */}
      <div className="px-4 py-2 space-y-1">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1 mt-1">Einstellungen</div>
        {menu.map((label, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-2 rounded-md bg-white/[0.025] border border-white/5">
            <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: `${accent}15`, border: `1px solid ${accent}25` }}>
              {sectionGlyph(label, accent, 11)}
            </div>
            <div className="flex-1 text-[8px] font-['Outfit'] text-white truncate">{shortName(label, 30)}</div>
            <span className="text-[9px] text-white/30">›</span>
          </div>
        ))}
      </div>

      {/* Logout */}
      <div className="px-4 py-3">
        <div className="px-2 py-2 rounded-md text-center text-[8px] font-['Outfit'] font-medium border" style={{ borderColor: "rgba(255,111,111,0.3)", background: "rgba(255,111,111,0.08)", color: "#FF6F6F" }}>Abmelden</div>
        <div className="text-center text-[5.5px] font-['JetBrains_Mono'] text-white/30 mt-2">{shortName(appName, 24)} · v2.4.1</div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · MOBILE-FITNESS — Workout / Activity detail
// ============================================================
function DetailFitness({ project, accent }) {
  const workoutName = pickName(frontendOf(project)[0], "Morgenlauf");
  return (
    <div className="bg-[#05050A]" style={{ minHeight: 480 }}>
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <span className="text-[10px] text-white/60">←</span>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white text-center">{shortName(workoutName, 24)}</div>
        <span className="text-[10px] text-white/60">⤴</span>
      </div>

      {/* Hero stats */}
      <div className="px-4 py-4 text-center" style={{ background: `radial-gradient(ellipse at top, ${accent}25, transparent 70%)` }}>
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">DISTANZ</div>
        <div className="font-['Outfit'] text-white font-light leading-none mt-1" style={{ fontSize: 36 }}>
          5.42<span className="text-[14px] text-white/60 ml-1">km</span>
        </div>
        <div className="flex items-center justify-center gap-4 mt-3">
          <div>
            <div className="text-white font-['Outfit'] text-[13px] font-light">28:14</div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Zeit</div>
          </div>
          <div className="w-px h-7 bg-white/10" />
          <div>
            <div className="font-['Outfit'] text-[13px] font-light" style={{ color: accent }}>5:12</div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Pace /km</div>
          </div>
          <div className="w-px h-7 bg-white/10" />
          <div>
            <div className="text-white font-['Outfit'] text-[13px] font-light">412</div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">kcal</div>
          </div>
        </div>
      </div>

      {/* Map placeholder */}
      <div className="mx-4 mb-3 rounded-md overflow-hidden border" style={{ borderColor: `${accent}28` }}>
        <div className="relative aspect-[16/8]" style={{ background: `linear-gradient(135deg, #0F1A2A, #051018)` }}>
          <div className={`absolute inset-0 ${grid()}`} />
          {/* Route SVG */}
          <svg viewBox="0 0 320 140" className="absolute inset-0 w-full h-full">
            <path d="M20 110 Q60 30, 120 70 T220 50 T300 90" stroke={accent} strokeWidth="3" fill="none" strokeLinecap="round" strokeDasharray="0" />
            <circle cx="20" cy="110" r="5" fill={accent} />
            <circle cx="300" cy="90" r="5" fill="#fff" stroke={accent} strokeWidth="2" />
          </svg>
          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: "rgba(0,0,0,0.6)", color: accent }}>GPS · LIVE</div>
        </div>
      </div>

      {/* Heart rate chart */}
      <div className="px-4 pb-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[7px] font-['Outfit'] text-white">Herzfrequenz</div>
          <div className="text-[7px] font-['JetBrains_Mono']" style={{ color: accent }}>Ø 148 bpm · max 172</div>
        </div>
        <div className="rounded-md p-2 bg-white/[0.025] border border-white/5">
          <svg viewBox="0 0 320 60" className="w-full h-12">
            <polyline points="0,40 25,30 50,25 75,28 100,15 125,22 150,18 175,30 200,20 225,35 250,28 275,32 300,25 320,30" fill="none" stroke={accent} strokeWidth="1.5" strokeLinejoin="round" />
            <polyline points="0,40 25,30 50,25 75,28 100,15 125,22 150,18 175,30 200,20 225,35 250,28 275,32 300,25 320,30 320,60 0,60" fill={`${accent}15`} />
          </svg>
        </div>
      </div>

      {/* Splits */}
      <div className="px-4 pb-3">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1.5">Splits</div>
        {[
          { km: "1", t: "5:18", pace: "+6" },
          { km: "2", t: "5:08", pace: "−4" },
          { km: "3", t: "5:02", pace: "−10", best: true },
          { km: "4", t: "5:14", pace: "+2" },
          { km: "5", t: "5:18", pace: "+6" },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-2 py-1 border-t border-white/5 first:border-t-0">
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-['JetBrains_Mono'] font-bold" style={{ background: s.best ? accent : "rgba(255,255,255,0.06)", color: s.best ? "#000" : "rgba(255,255,255,0.7)" }}>{s.km}</div>
            <div className="text-[8px] font-['Outfit'] text-white">{s.t}</div>
            <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full" style={{ background: accent, width: `${50 + i * 8}%` }} />
            </div>
            <div className="text-[7px] font-['JetBrains_Mono']" style={{ color: s.pace.startsWith("−") ? accent : "rgba(255,255,255,0.5)" }}>{s.pace}s</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="px-4 pb-3 flex gap-1.5">
        <div className="flex-1 px-2 py-1.5 rounded text-center text-[8px] font-['Outfit'] border border-white/10 text-white/70">Teilen</div>
        <div className="flex-1 px-2 py-1.5 rounded text-center text-[8px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Speichern</div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · MOBILE-SOCIAL — Feed post detail
// ============================================================
function DetailSocial({ project, accent }) {
  const topic = pickName(frontendOf(project)[0], project?.projectTitle || "Community");
  return (
    <div className="bg-[#05050A]" style={{ minHeight: 480 }}>
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <span className="text-[10px] text-white/60">←</span>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white text-center">Beitrag</div>
        <span className="text-[10px] text-white/60">⋯</span>
      </div>

      {/* Author */}
      <div className="px-4 py-2.5 flex items-center gap-2">
        <div className="w-9 h-9 rounded-full flex items-center justify-center font-['Outfit'] text-[10px] font-light text-black" style={{ background: accent }}>LM</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-[9px] font-['Outfit'] text-white">Lukas Meier</span>
            <span className="text-[8px]" style={{ color: accent }}>✓</span>
          </div>
          <div className="text-[6.5px] font-['JetBrains_Mono'] text-white/40">@lukasm · vor 12 Min · 📍 Zürich</div>
        </div>
        <div className="px-2.5 py-1 rounded text-[7px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Folgen</div>
      </div>

      {/* Content */}
      <div className="px-4 pb-2">
        <div className="text-[9px] font-['Outfit'] text-white/90 leading-relaxed">
          Heute über {shortName(topic, 30)} diskutiert — wirklich spannend, was aktuell passiert. <span style={{ color: accent }}>#community</span> <span style={{ color: accent }}>#schweiz</span>
        </div>
      </div>

      {/* Media */}
      <div className="relative mx-4 mb-2 rounded-md overflow-hidden aspect-[4/3]" style={{ background: `linear-gradient(135deg, ${accent}30, ${accent}05)` }}>
        <div className={`absolute inset-0 ${grid()}`} />
        <div className="absolute inset-0 flex items-center justify-center">{sectionGlyph(topic, accent, 72)}</div>
        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}>1 / 4</div>
      </div>

      {/* Engagement */}
      <div className="px-4 py-1.5 flex items-center gap-4 border-t border-white/5">
        <div className="flex items-center gap-1">
          <span className="text-[12px]" style={{ color: accent }}>♥</span>
          <span className="text-[8px] font-['JetBrains_Mono'] text-white">2'847</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-white/60">💬</span>
          <span className="text-[8px] font-['JetBrains_Mono'] text-white/70">184</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-white/60">↻</span>
          <span className="text-[8px] font-['JetBrains_Mono'] text-white/70">52</span>
        </div>
        <span className="ml-auto text-[11px] text-white/60">⤴</span>
        <span className="text-[11px] text-white/60">⌖</span>
      </div>

      {/* Comments */}
      <div className="px-4 py-2 space-y-1.5">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Kommentare</div>
        {[
          { n: "Anna B.", t: "Sehe ich genauso 👍", l: "23" },
          { n: "Mike R.", t: "Top Beitrag, danke fürs Teilen!", l: "12" },
          { n: "Eva K.", t: "Spannender Punkt zum Schluss.", l: "4" },
        ].map((c, i) => (
          <div key={i} className="flex items-start gap-1.5">
            <div className="w-6 h-6 rounded-full flex-shrink-0" style={{ background: `${accent}40` }} />
            <div className="flex-1 min-w-0 rounded-lg px-2 py-1 bg-white/[0.04]">
              <div className="text-[7.5px] font-['Outfit'] text-white font-medium">{c.n}</div>
              <div className="text-[8px] font-['Outfit'] text-white/80 leading-snug">{c.t}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[6.5px] font-['JetBrains_Mono'] text-white/40">♥ {c.l}</span>
                <span className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: accent }}>Antworten</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Comment input */}
      <div className="px-3 py-2 border-t border-white/5 flex items-center gap-1.5 mt-1">
        <div className="w-6 h-6 rounded-full" style={{ background: accent }} />
        <div className="flex-1 px-2 py-1.5 rounded-full bg-white/[0.04] border border-white/8 text-[7px] font-['Outfit'] text-white/40">Kommentar schreiben…</div>
        <span className="text-[10px]" style={{ color: accent }}>➤</span>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · MOBILE-BOOKING — Service / Slot booking
// ============================================================
function DetailBooking({ project, accent }) {
  const service = pickName(frontendOf(project)[0], project?.projectTitle || "Service-Termin");
  return (
    <div className="bg-[#05050A]" style={{ minHeight: 480 }}>
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <span className="text-[10px] text-white/60">←</span>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white text-center">Buchen</div>
        <span className="text-[10px] text-white/60">♡</span>
      </div>

      {/* Hero */}
      <div className="relative aspect-[16/8] overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}28, ${accent}05 70%)` }}>
        <div className={`absolute inset-0 ${grid()}`} />
        <div className="absolute inset-0 flex items-center justify-center">{sectionGlyph(service, accent, 64)}</div>
        <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono'] font-bold" style={{ background: accent, color: "#000" }}>BELIEBT</div>
      </div>

      {/* Service header */}
      <div className="px-4 py-3 border-b border-white/5">
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Premium Service</div>
        <div className="text-white font-['Outfit'] font-light text-[14px] leading-tight mt-0.5">{shortName(service, 36)}</div>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex">{[0, 1, 2, 3, 4].map((i) => <span key={i} className="text-[7px]" style={{ color: i < 5 ? accent : "rgba(255,255,255,0.2)" }}>★</span>)}</div>
          <span className="text-[6.5px] font-['JetBrains_Mono'] text-white/50">4.9 · 847 Buchungen</span>
        </div>
        <div className="flex items-baseline gap-2 mt-1.5">
          <span className="text-white font-['Outfit'] font-light text-[15px] leading-none">CHF 145.–</span>
          <span className="text-[7.5px] text-white/50 font-['Outfit']">/ 60 Min</span>
        </div>
      </div>

      {/* Date picker */}
      <div className="px-4 py-3 border-b border-white/5">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1.5">Datum wählen</div>
        <div className="grid grid-cols-6 gap-1">
          {[
            { d: "Mo", n: "10", on: true },
            { d: "Di", n: "11", active: true },
            { d: "Mi", n: "12", on: true },
            { d: "Do", n: "13", on: true },
            { d: "Fr", n: "14", on: false },
            { d: "Sa", n: "15", on: true },
          ].map((dt, i) => (
            <div key={i} className="text-center py-1.5 rounded-md border" style={{
              background: dt.active ? accent : "rgba(255,255,255,0.025)",
              borderColor: dt.active ? accent : "rgba(255,255,255,0.08)",
              opacity: dt.on || dt.active ? 1 : 0.35,
            }}>
              <div className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: dt.active ? "#000" : "rgba(255,255,255,0.5)" }}>{dt.d}</div>
              <div className="font-['Outfit'] text-[12px] font-light" style={{ color: dt.active ? "#000" : "#fff" }}>{dt.n}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Time slots */}
      <div className="px-4 py-3 border-b border-white/5">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Verfügbare Slots</div>
          <div className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: accent }}>Di · 11. Feb</div>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {["08:00", "09:30", "11:00", "13:30", "14:30", "15:30", "16:30", "18:00"].map((t, i) => (
            <div key={i} className="text-center py-1.5 rounded-md text-[7.5px] font-['Outfit'] border" style={{
              background: i === 3 ? `${accent}25` : "rgba(255,255,255,0.025)",
              borderColor: i === 3 ? accent : "rgba(255,255,255,0.08)",
              color: i === 3 ? accent : i === 5 ? "rgba(255,255,255,0.25)" : "#fff",
              textDecoration: i === 5 ? "line-through" : "none",
            }}>{t}</div>
          ))}
        </div>
      </div>

      {/* Summary + CTA */}
      <div className="px-4 py-3 flex items-center gap-2">
        <div className="flex-1">
          <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Auswahl</div>
          <div className="text-[9px] font-['Outfit'] text-white">Di · 11. Feb · 13:30</div>
        </div>
        <div className="px-3 py-1.5 rounded text-[8px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Slot reservieren →</div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · CLOUD — Infrastructure dashboard
// ============================================================
function DetailCloud({ project, accent }) {
  const region = pickName(frontendOf(project)[0], project?.projectTitle || "Production");
  return (
    <div className="bg-[#05050A]">
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>INFRASTRUKTUR</div>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white truncate">{shortName(region, 36)} · Live-Migration</div>
        <span className="flex items-center gap-1 text-[6.5px] font-['JetBrains_Mono']" style={{ color: "#28C840" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#28C840" }} />HEALTHY
        </span>
      </div>

      {/* Top KPIs */}
      <div className="px-4 py-3 grid grid-cols-4 gap-2">
        {[
          { l: "Uptime SLA", v: "99.98%", trend: "+0.02" },
          { l: "Migriert", v: "67%", trend: "82/124" },
          { l: "RTO", v: "4 Min", trend: "Ziel 15" },
          { l: "RPO", v: "60 Sek", trend: "Ziel 5 Min" },
        ].map((k, i) => (
          <div key={i} className="rounded-md p-2 bg-white/[0.03] border border-white/8">
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">{k.l}</div>
            <div className="text-white font-['Outfit'] text-[14px] font-light leading-tight mt-0.5">{k.v}</div>
            <div className="text-[6.5px] font-['JetBrains_Mono'] mt-0.5" style={{ color: accent }}>{k.trend}</div>
          </div>
        ))}
      </div>

      {/* Migration progress */}
      <div className="px-4 py-2">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[7.5px] font-['Outfit'] text-white">Workload-Migration · Phase 2/3</div>
          <div className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: accent }}>ETA 4d 12h</div>
        </div>
        <div className="rounded-md p-2.5 bg-white/[0.025] border border-white/5 space-y-1.5">
          {[
            { n: "App-Server (k8s)", s: 100, st: "DONE" },
            { n: "Database-Cluster", s: 78, st: "RUNNING" },
            { n: "Object-Storage", s: 45, st: "RUNNING" },
            { n: "Legacy-API-Gateway", s: 0, st: "QUEUED" },
          ].map((w, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[7.5px] font-['Outfit'] text-white/85 truncate">{w.n}</span>
                  <span className="text-[6px] font-['JetBrains_Mono']" style={{ color: w.s === 100 ? accent : w.s === 0 ? "rgba(255,255,255,0.3)" : "#FFB800" }}>{w.st}</span>
                </div>
                <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full" style={{ background: w.s === 100 ? accent : "#FFB800", width: `${w.s}%` }} />
                </div>
              </div>
              <span className="text-[7px] font-['JetBrains_Mono'] text-white/60 w-8 text-right">{w.s}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Region map */}
      <div className="px-4 py-2 grid grid-cols-3 gap-2">
        {[
          { l: "eu-central-1", c: "Frankfurt", s: "PRIMARY", on: true },
          { l: "eu-west-3", c: "Zürich", s: "FAILOVER", on: true },
          { l: "us-east-1", c: "Virginia", s: "OFFLINE", on: false },
        ].map((r, i) => (
          <div key={i} className="rounded-md p-2 border" style={{
            background: r.on ? `${accent}08` : "rgba(255,255,255,0.025)",
            borderColor: r.on ? `${accent}30` : "rgba(255,255,255,0.08)",
          }}>
            <div className="flex items-center gap-1 mb-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.on ? "#28C840" : "#FF6F6F" }} />
              <span className="text-[6.5px] font-['JetBrains_Mono'] text-white/70">{r.l}</span>
            </div>
            <div className="text-[9px] font-['Outfit'] text-white">{r.c}</div>
            <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono'] mt-0.5" style={{ color: r.on ? accent : "rgba(255,255,255,0.4)" }}>{r.s}</div>
          </div>
        ))}
      </div>

      {/* Resource usage */}
      <div className="px-4 py-2 pb-3">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1.5">Ressourcen-Auslastung (24h)</div>
        <div className="rounded-md p-2 bg-white/[0.025] border border-white/5">
          <svg viewBox="0 0 320 50" className="w-full h-10">
            <polyline points="0,35 20,30 40,25 60,28 80,18 100,22 120,28 140,15 160,20 180,12 200,18 220,25 240,22 260,28 280,20 300,24 320,18" fill="none" stroke={accent} strokeWidth="1.5" strokeLinejoin="round" />
            <polyline points="0,42 20,40 40,38 60,40 80,32 100,38 120,40 140,30 160,34 180,28 200,32 220,38 240,34 260,40 280,32 300,36 320,30" fill="none" stroke="#FFB800" strokeWidth="1" strokeLinejoin="round" strokeDasharray="2 2" />
          </svg>
          <div className="flex items-center gap-3 mt-1">
            <span className="flex items-center gap-1 text-[6px] font-['JetBrains_Mono']" style={{ color: accent }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />CPU 42%</span>
            <span className="flex items-center gap-1 text-[6px] font-['JetBrains_Mono'] text-[#FFB800]"><span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />RAM 68%</span>
            <span className="ml-auto text-[6px] font-['JetBrains_Mono'] text-white/40">Auto-Scaling AKTIV</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · DATA — Analytics / AI hub
// ============================================================
function DetailData({ project, accent }) {
  const datasetName = pickName(frontendOf(project)[0], project?.projectTitle || "Sales-Pipeline");
  return (
    <div className="bg-[#05050A]">
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>DATA & AI</div>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white truncate">{shortName(datasetName, 36)}</div>
        <div className="px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: `${accent}20`, color: accent }}>● LIVE</div>
      </div>

      {/* KPI tiles */}
      <div className="px-4 py-3 grid grid-cols-4 gap-2">
        {[
          { l: "Datensätze", v: "1.24M", trend: "+8.2%" },
          { l: "AI-Anfragen", v: "47k", trend: "+22%" },
          { l: "Genauigkeit", v: "94.7%", trend: "+1.3 pp" },
          { l: "Latenz Ø", v: "82ms", trend: "−14 ms" },
        ].map((k, i) => (
          <div key={i} className="rounded-md p-2 bg-white/[0.03] border border-white/8">
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">{k.l}</div>
            <div className="text-white font-['Outfit'] text-[14px] font-light leading-tight mt-0.5">{k.v}</div>
            <div className="text-[6.5px] font-['JetBrains_Mono'] mt-0.5" style={{ color: accent }}>↑ {k.trend}</div>
          </div>
        ))}
      </div>

      {/* Chart panel */}
      <div className="px-4 py-2">
        <div className="rounded-md p-2.5 bg-white/[0.025] border border-white/5">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[7.5px] font-['Outfit'] text-white">Predictive Forecast · Q1 2026</div>
            <div className="flex items-center gap-2 text-[6px] font-['JetBrains_Mono']">
              <span style={{ color: accent }}>● Actual</span>
              <span className="text-[#FFB800]">● Forecast</span>
            </div>
          </div>
          <svg viewBox="0 0 320 90" className="w-full h-20">
            {/* Grid lines */}
            {[0, 1, 2, 3].map((i) => (
              <line key={i} x1="0" y1={20 + i * 18} x2="320" y2={20 + i * 18} stroke="rgba(255,255,255,0.04)" />
            ))}
            {/* Actual */}
            <polyline points="0,75 40,65 80,55 120,48 160,40 200,30" fill="none" stroke={accent} strokeWidth="2" strokeLinejoin="round" />
            {/* Forecast (dashed) */}
            <polyline points="200,30 240,22 280,18 320,12" fill="none" stroke="#FFB800" strokeWidth="2" strokeDasharray="3 3" strokeLinejoin="round" />
            {/* Confidence band */}
            <polygon points="200,38 240,28 280,22 320,16 320,8 280,14 240,16 200,22" fill="#FFB800" opacity="0.12" />
            <circle cx="200" cy="30" r="3" fill={accent} />
          </svg>
          <div className="flex items-center gap-3 text-[6px] font-['JetBrains_Mono'] text-white/40 mt-1">
            <span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span><span>May</span><span>Jun</span>
          </div>
        </div>
      </div>

      {/* AI insight card + Pipeline */}
      <div className="px-4 py-2 grid grid-cols-2 gap-2">
        <div className="rounded-md p-2 border" style={{ background: `${accent}08`, borderColor: `${accent}30` }}>
          <div className="flex items-center gap-1 mb-1">
            <div className="w-4 h-4 rounded flex items-center justify-center" style={{ background: accent }}><span className="text-[7px] font-bold text-black">AI</span></div>
            <span className="text-[7px] font-['Outfit'] text-white">Insight · vor 2 Min</span>
          </div>
          <div className="text-[7.5px] font-['Outfit'] text-white/85 leading-snug">
            Conversion-Rate steigt 23 % bei <strong style={{ color: accent }}>{shortName(datasetName, 18)}</strong> in DACH.
          </div>
          <div className="text-[6.5px] font-['JetBrains_Mono'] mt-1.5" style={{ color: accent }}>Detail-Analyse öffnen →</div>
        </div>
        <div className="rounded-md p-2 bg-white/[0.025] border border-white/5">
          <div className="text-[7px] font-['Outfit'] text-white mb-1">Pipeline-Status</div>
          <div className="space-y-1">
            {[
              { n: "Ingest", s: "OK" },
              { n: "Transform", s: "OK" },
              { n: "ML-Model", s: "RUNNING" },
              { n: "Publish", s: "QUEUED" },
            ].map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.s === "OK" ? accent : p.s === "RUNNING" ? "#FFB800" : "rgba(255,255,255,0.2)" }} />
                <span className="text-[7px] font-['Outfit'] text-white/75 flex-1">{p.n}</span>
                <span className="text-[6px] font-['JetBrains_Mono']" style={{ color: p.s === "OK" ? accent : p.s === "RUNNING" ? "#FFB800" : "rgba(255,255,255,0.4)" }}>{p.s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Query bar */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-1.5 rounded-md px-2 py-1.5 border" style={{ background: "rgba(255,255,255,0.025)", borderColor: `${accent}30` }}>
          <span className="text-[10px]" style={{ color: accent }}>⌕</span>
          <span className="text-[7.5px] font-['JetBrains_Mono'] text-white/60 flex-1 truncate">SELECT * FROM customers WHERE region = 'DACH' AND ai_score &gt; 0.8</span>
          <span className="px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: accent, color: "#000" }}>RUN</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · SECURITY — Audit / Compliance overview
// ============================================================
function DetailSecurity({ project, accent }) {
  const auditName = pickName(frontendOf(project)[0], project?.projectTitle || "Q1 Security-Audit");
  // donut chart calc
  const score = 87;
  const radius = 22;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;
  return (
    <div className="bg-[#05050A]">
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>SECURITY</div>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white truncate">{shortName(auditName, 36)}</div>
        <div className="px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono']" style={{ background: "rgba(255,184,0,0.15)", color: "#FFB800" }}>⚠ 3 OFFEN</div>
      </div>

      {/* Compliance score donut + frameworks */}
      <div className="px-4 py-3 grid grid-cols-5 gap-3">
        <div className="col-span-2 flex items-center justify-center">
          <div className="relative">
            <svg viewBox="0 0 60 60" width="100" height="100">
              <circle cx="30" cy="30" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
              <circle cx="30" cy="30" r={radius} fill="none" stroke={accent} strokeWidth="4" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} transform="rotate(-90 30 30)" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-white font-['Outfit'] font-light text-[18px] leading-none">{score}</div>
              <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mt-0.5">SCORE</div>
            </div>
          </div>
        </div>
        <div className="col-span-3 space-y-1">
          <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1">Frameworks</div>
          {[
            { n: "ISO 27001", s: 92, ok: true },
            { n: "GDPR / nDSG", s: 88, ok: true },
            { n: "SOC 2 Type II", s: 81, ok: true },
            { n: "PCI-DSS", s: 64, ok: false },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[7.5px] font-['Outfit'] text-white/85 w-24">{f.n}</span>
              <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ background: f.ok ? accent : "#FFB800", width: `${f.s}%` }} />
              </div>
              <span className="text-[7px] font-['JetBrains_Mono'] w-8 text-right" style={{ color: f.ok ? accent : "#FFB800" }}>{f.s}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Open findings */}
      <div className="px-4 py-2 border-t border-white/5">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[7.5px] font-['Outfit'] text-white">Offene Findings</div>
          <div className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: accent }}>Alle anzeigen →</div>
        </div>
        <div className="space-y-1.5">
          {[
            { sev: "HIGH", c: "#FF6F6F", t: "TLS 1.0 noch aktiv auf 2 Endpoints", a: "Networking · sofort" },
            { sev: "MED", c: "#FFB800", t: "Veraltete Bibliothek `log4j-core` 2.14.x", a: "Backend · bis 28.02" },
            { sev: "LOW", c: "#9D8DF1", t: "MFA-Erinnerung für 12 User ausstehend", a: "IAM · bis 15.03" },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-2 rounded-md p-1.5 bg-white/[0.025] border border-white/5">
              <span className="px-1.5 py-0.5 rounded text-[6px] font-['JetBrains_Mono'] font-bold flex-shrink-0" style={{ background: `${f.c}22`, color: f.c, border: `1px solid ${f.c}55` }}>{f.sev}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[8px] font-['Outfit'] text-white truncate">{f.t}</div>
                <div className="text-[6.5px] font-['JetBrains_Mono'] text-white/40">{f.a}</div>
              </div>
              <span className="text-[9px] text-white/40">›</span>
            </div>
          ))}
        </div>
      </div>

      {/* Activity timeline */}
      <div className="px-4 py-3 border-t border-white/5">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1.5">Letzte Events</div>
        <div className="space-y-1">
          {[
            { t: "10:42", e: "Pentest abgeschlossen — 0 kritische Fehler", ok: true },
            { t: "09:15", e: "12 fehlgeschlagene Logins von 1 IP", ok: false },
            { t: "Gestern", e: "ISO-27001-Re-Audit bestanden", ok: true },
          ].map((e, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: e.ok ? accent : "#FF6F6F" }} />
              <span className="text-[6.5px] font-['JetBrains_Mono'] text-white/40 w-12">{e.t}</span>
              <span className="text-[7.5px] font-['Outfit'] text-white/80 flex-1 truncate">{e.e}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · INTEGRATION — Workflow / iPaaS builder
// ============================================================
function DetailIntegration({ project, accent }) {
  const flowName = pickName(frontendOf(project)[0], project?.projectTitle || "Order-Sync");
  return (
    <div className="bg-[#05050A]">
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>WORKFLOW</div>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white truncate">{shortName(flowName, 36)}</div>
        <div className="flex items-center gap-1 text-[6.5px] font-['JetBrains_Mono']" style={{ color: accent }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />AKTIV
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 grid grid-cols-4 gap-2">
        {[
          { l: "Runs / Tag", v: "12'847" },
          { l: "Erfolg", v: "99.6%" },
          { l: "Ø Dauer", v: "1.8s" },
          { l: "Fehler 24h", v: "47" },
        ].map((k, i) => (
          <div key={i} className="rounded-md p-2 bg-white/[0.03] border border-white/8">
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">{k.l}</div>
            <div className="text-white font-['Outfit'] text-[14px] font-light leading-tight mt-0.5">{k.v}</div>
          </div>
        ))}
      </div>

      {/* Flow canvas */}
      <div className="px-4 py-2">
        <div className="rounded-md p-3 bg-white/[0.025] border border-white/5 relative overflow-hidden" style={{ minHeight: 140 }}>
          <div className={`absolute inset-0 ${grid()} opacity-50`} />
          <svg viewBox="0 0 320 140" className="absolute inset-0 w-full h-full">
            <defs>
              <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M0,0 L10,5 L0,10 z" fill={accent} />
              </marker>
            </defs>
            {/* connections */}
            <path d="M62,40 L130,40" stroke={accent} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)" />
            <path d="M62,40 Q90,40 90,75 T130,100" stroke={accent} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)" strokeDasharray="2 2" />
            <path d="M192,40 L258,40" stroke={accent} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)" />
            <path d="M192,100 L258,100" stroke={accent} strokeWidth="1.5" fill="none" markerEnd="url(#arrow)" />
          </svg>
          {/* Nodes */}
          <div className="absolute left-2 top-6 w-14 rounded p-1.5 border text-center" style={{ background: "rgba(0,240,255,0.12)", borderColor: accent }}>
            <div className="text-[6px] font-['JetBrains_Mono']" style={{ color: accent }}>TRIGGER</div>
            <div className="text-[7.5px] font-['Outfit'] text-white font-medium">SAP S/4</div>
            <div className="text-[5.5px] text-white/40 font-['JetBrains_Mono']">ERP</div>
          </div>
          <div className="absolute left-[130px] top-6 w-16 rounded p-1.5 border text-center" style={{ background: "rgba(255,184,0,0.12)", borderColor: "#FFB800" }}>
            <div className="text-[6px] font-['JetBrains_Mono'] text-[#FFB800]">TRANSFORM</div>
            <div className="text-[7.5px] font-['Outfit'] text-white font-medium">Mapping</div>
            <div className="text-[5.5px] text-white/40 font-['JetBrains_Mono']">iPaaS</div>
          </div>
          <div className="absolute left-[130px] top-[88px] w-16 rounded p-1.5 border text-center" style={{ background: "rgba(157,141,241,0.12)", borderColor: "#9D8DF1" }}>
            <div className="text-[6px] font-['JetBrains_Mono'] text-[#9D8DF1]">FILTER</div>
            <div className="text-[7.5px] font-['Outfit'] text-white font-medium">Validation</div>
            <div className="text-[5.5px] text-white/40 font-['JetBrains_Mono']">Schema</div>
          </div>
          <div className="absolute left-[258px] top-6 w-14 rounded p-1.5 border text-center" style={{ background: "rgba(0,240,255,0.12)", borderColor: accent }}>
            <div className="text-[6px] font-['JetBrains_Mono']" style={{ color: accent }}>SINK</div>
            <div className="text-[7.5px] font-['Outfit'] text-white font-medium">Salesforce</div>
            <div className="text-[5.5px] text-white/40 font-['JetBrains_Mono']">CRM</div>
          </div>
          <div className="absolute left-[258px] top-[88px] w-14 rounded p-1.5 border text-center" style={{ background: "rgba(40,200,64,0.12)", borderColor: "#28C840" }}>
            <div className="text-[6px] font-['JetBrains_Mono'] text-[#28C840]">SINK</div>
            <div className="text-[7.5px] font-['Outfit'] text-white font-medium">Snowflake</div>
            <div className="text-[5.5px] text-white/40 font-['JetBrains_Mono']">DWH</div>
          </div>
        </div>
      </div>

      {/* Recent runs */}
      <div className="px-4 py-2 pb-3 border-t border-white/5 mt-1">
        <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1.5 mt-1">Letzte Ausführungen</div>
        <div className="space-y-1">
          {[
            { id: "RUN-7234", t: "vor 12 Sek", d: "1.6s", ok: true },
            { id: "RUN-7233", t: "vor 38 Sek", d: "2.1s", ok: true },
            { id: "RUN-7232", t: "vor 1 Min", d: "—", ok: false, msg: "Timeout @ Salesforce-API" },
            { id: "RUN-7231", t: "vor 2 Min", d: "1.4s", ok: true },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1 rounded bg-white/[0.025] border border-white/5">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: r.ok ? accent : "#FF6F6F" }} />
              <span className="text-[7px] font-['JetBrains_Mono'] text-white/70 w-16">{r.id}</span>
              <span className="text-[6.5px] font-['JetBrains_Mono'] text-white/40 w-16">{r.t}</span>
              <span className="text-[7.5px] font-['Outfit'] text-white/75 flex-1 truncate">{r.ok ? "Erfolgreich · 87 Records synchronisiert" : r.msg}</span>
              <span className="text-[6.5px] font-['JetBrains_Mono'] w-10 text-right" style={{ color: r.ok ? accent : "#FF6F6F" }}>{r.d}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// DETAIL · DEFAULT — Generic content/feature detail (legacy fallback)
// ============================================================
function DetailDefault({ project, accent }) {
  const sections = frontendOf(project);
  const main = sections[0];
  const mainName = pickName(main, "Smart Living Hub");
  const tags = (project?.analysis?.techStack_reco || []).slice(0, 4);
  const otherSections = sections.slice(1, 5);

  return (
    <div className="bg-[#05050A] flex flex-col">
      <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-white/8 flex items-center justify-center"><span className="text-[7px] text-white/60">←</span></div>
        <div className="flex-1 truncate">
          <div className="text-[5.5px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">{project?.projectCategory || "App"}</div>
          <div className="text-[10px] font-['Outfit'] text-white truncate">{shortName(mainName, 36)}</div>
        </div>
        <div className="w-5 h-5 rounded-full bg-white/8 border border-white/10" />
      </div>

      <div className="relative aspect-[16/7] overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}30, ${accent}05 70%)` }}>
        <div className={`absolute inset-0 ${grid()}`} />
        <div className="absolute inset-0 flex items-center justify-center">{sectionGlyph(mainName, accent, 64)}</div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[5.5px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Empfohlen</div>
            <div className="text-white font-['Outfit'] font-light text-[14px] leading-tight">{shortName(mainName, 40)}</div>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex">{[0, 1, 2, 3, 4].map((i) => <span key={i} className="text-[7px]" style={{ color: i < 4 ? accent : "rgba(255,255,255,0.2)" }}>★</span>)}</div>
              <span className="text-[6.5px] font-['JetBrains_Mono'] text-white/50">4.8 · 1.2k</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">ab</div>
            <div className="text-white font-['Outfit'] font-light text-[14px] leading-none">CHF 0</div>
            <div className="text-[6px] text-white/50 font-['Outfit']">/ Monat</div>
          </div>
        </div>

        <div className="space-y-1">
          {[
            "Sofortige Aktivierung — keine Wartezeit",
            "Premium-Support rund um die Uhr",
            "Volle Kontrolle in deinem Konto",
            "Kostenlos kündbar, jederzeit",
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${accent}20`, border: `1px solid ${accent}50` }}>
                <svg viewBox="0 0 24 24" width="8" height="8" fill="none" stroke={accent} strokeWidth="3"><path d="M5 13 l4 4 L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
              <div className="text-[7.5px] font-['Outfit'] text-white/85">{f}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-1 flex-wrap">
          {tags.map((tg, i) => (
            <span key={i} className="px-1.5 py-0.5 rounded text-[6.5px] font-['JetBrains_Mono'] truncate max-w-[100px]" style={{ background: `${accent}10`, color: accent, border: `1px solid ${accent}25` }}>{tg}</span>
          ))}
        </div>

        {otherSections.length > 0 && (
          <div>
            <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-1.5">Auch interessant</div>
            <div className="grid grid-cols-2 gap-1.5">
              {otherSections.slice(0, 4).map((it, i) => (
                <div key={i} className="rounded p-1.5 bg-white/[0.025] border border-white/5 flex items-center gap-1.5">
                  <div className="w-6 h-6 rounded flex-shrink-0 flex items-center justify-center" style={{ background: `${accent}15`, border: `1px solid ${accent}30` }}>
                    {sectionGlyph(pickName(it), accent, 12)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[7px] font-['Outfit'] text-white truncate font-medium">{shortName(pickName(it), 22)}</div>
                    <div className="text-[6px] font-['JetBrains_Mono']" style={{ color: accent }}>Öffnen →</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-white/8 flex items-center justify-between gap-2">
        <div>
          <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Inklusive in</div>
          <div className="text-[8px] font-['Outfit'] text-white">Premium Plus</div>
        </div>
        <div className="px-4 py-1.5 rounded text-[7.5px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Jetzt aktivieren →</div>
      </div>
    </div>
  );
}

// Detail dispatcher — picks the right variant based on project category
function DetailVariant({ project, accent }) {
  const v = detectVertical(project);
  if (v === "ecommerce") return <DetailEcommerce project={project} accent={accent} />;
  if (v === "fintech") return <DetailFintech project={project} accent={accent} />;
  if (v === "saas") return <DetailSaas project={project} accent={accent} />;
  if (v === "health") return <DetailHealth project={project} accent={accent} />;
  // Mobile sub-variants
  if (v === "mobile-chat") return <DetailMobile project={project} accent={accent} />;
  if (v === "mobile-fitness") return <DetailFitness project={project} accent={accent} />;
  if (v === "mobile-social") return <DetailSocial project={project} accent={accent} />;
  if (v === "mobile-booking") return <DetailBooking project={project} accent={accent} />;
  if (v === "mobile-profile") return <DetailProfile project={project} accent={accent} />;
  // Enterprise variants
  if (v === "cloud") return <DetailCloud project={project} accent={accent} />;
  if (v === "data") return <DetailData project={project} accent={accent} />;
  if (v === "security") return <DetailSecurity project={project} accent={accent} />;
  if (v === "integration") return <DetailIntegration project={project} accent={accent} />;
  return <DetailDefault project={project} accent={accent} />;
}

// ============================================================
// DETAIL · FLOW — Generic 3-step wizard (uses real project features)
// ============================================================
function DetailFlow({ project, accent }) {
  const sections = frontendOf(project);
  const f1 = shortName(pickName(sections[0], "Auswahl treffen"), 28);
  const f2 = shortName(pickName(sections[1], "Konfiguration anpassen"), 28);
  const f3 = shortName(pickName(sections[2], "Bestätigung & Versand"), 28);
  const steps = [
    { n: 1, t: f1, d: "Wähle aus den verfügbaren Optionen die passende für dein Vorhaben aus." },
    { n: 2, t: f2, d: "Passe die Details an — alle Eingaben werden live validiert." },
    { n: 3, t: f3, d: "Prüfe deine Eingaben und schliesse den Vorgang sicher ab." },
  ];
  const activeIdx = 1;

  return (
    <div className="bg-[#05050A]">
      {/* Top bar */}
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <span className="text-[10px] text-white/60">←</span>
        <div className="flex-1 text-[9px] font-['Outfit'] text-white">Schritt {activeIdx + 1} von 3</div>
        <span className="text-[7px] font-['JetBrains_Mono']" style={{ color: accent }}>Speichern & beenden</span>
      </div>

      {/* Stepper */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex items-center gap-1">
          {steps.map((s, i) => {
            const done = i < activeIdx;
            const active = i === activeIdx;
            return (
              <div key={i} className="flex items-center gap-1 flex-1">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-['JetBrains_Mono'] text-[8px] font-bold"
                  style={{
                    background: done || active ? accent : "rgba(255,255,255,0.04)",
                    color: done || active ? "#000" : "rgba(255,255,255,0.4)",
                    border: `1px solid ${done || active ? accent : "rgba(255,255,255,0.1)"}`,
                  }}
                >
                  {done ? "✓" : s.n}
                </div>
                {i < steps.length - 1 && (
                  <div className="flex-1 h-px" style={{ background: done ? accent : "rgba(255,255,255,0.08)" }} />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-1 mt-1.5">
          {steps.map((s, i) => (
            <div key={i} className="flex-1 text-[6.5px] font-['Outfit']" style={{ color: i === activeIdx ? "#fff" : i < activeIdx ? accent : "rgba(255,255,255,0.4)" }}>{s.t}</div>
          ))}
        </div>
      </div>

      {/* Active step content */}
      <div className="px-6 py-3">
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Aktueller Schritt</div>
        <div className="text-white font-['Outfit'] font-light text-[16px] leading-tight mt-1">{steps[activeIdx].t}</div>
        <div className="text-[8px] font-['Outfit'] text-white/60 mt-1.5 leading-relaxed max-w-md">{steps[activeIdx].d}</div>

        {/* Option cards */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { l: "Standard", p: "CHF 0", d: "Basis-Funktionen", sel: false },
            { l: "Premium", p: "CHF 19", d: "Erweitert + Support", sel: true, tag: "EMPFOHLEN" },
            { l: "Enterprise", p: "CHF 49", d: "SLA + Custom", sel: false },
          ].map((o, i) => (
            <div key={i} className="rounded-md p-2 border relative" style={{
              background: o.sel ? `${accent}15` : "rgba(255,255,255,0.025)",
              borderColor: o.sel ? accent : "rgba(255,255,255,0.08)",
            }}>
              {o.tag && <div className="absolute -top-1.5 left-1.5 px-1 py-0.5 rounded text-[5.5px] font-['JetBrains_Mono'] font-bold" style={{ background: accent, color: "#000" }}>{o.tag}</div>}
              <div className="flex items-center justify-between">
                <div className="text-[8px] font-['Outfit'] text-white font-medium">{o.l}</div>
                <div className="w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center" style={{ borderColor: o.sel ? accent : "rgba(255,255,255,0.2)" }}>
                  {o.sel && <div className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />}
                </div>
              </div>
              <div className="text-white font-['Outfit'] font-light text-[12px] mt-1">{o.p}</div>
              <div className="text-[6.5px] font-['Outfit'] text-white/50 mt-0.5">{o.d}</div>
            </div>
          ))}
        </div>

        {/* Inline form fields */}
        <div className="mt-4 space-y-2">
          {[
            { l: "E-Mail-Adresse", v: "sarah.keller@mail.ch", valid: true },
            { l: "Telefon", v: "+41 79 234 56 78", valid: true },
            { l: "Promo-Code (optional)", v: "WELCOME26", valid: true },
          ].map((f, i) => (
            <div key={i}>
              <div className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] mb-0.5">{f.l}</div>
              <div className="px-2 py-1.5 rounded-md flex items-center gap-1.5 border" style={{ background: "rgba(255,255,255,0.025)", borderColor: f.valid ? `${accent}30` : "rgba(255,255,255,0.08)" }}>
                <span className="flex-1 text-[8px] font-['JetBrains_Mono'] text-white">{f.v}</span>
                {f.valid && <span className="text-[8px]" style={{ color: accent }}>✓</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer CTA */}
      <div className="px-4 py-2.5 border-t border-white/8 flex items-center gap-2">
        <div className="px-3 py-1.5 rounded text-[7.5px] font-['Outfit'] text-white/70 border border-white/10">← Zurück</div>
        <div className="flex-1 text-[6.5px] font-['JetBrains_Mono'] text-white/40 text-center">Speichern auch ohne Abschluss möglich</div>
        <div className="px-4 py-1.5 rounded text-[7.5px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Weiter →</div>
      </div>
    </div>
  );
}

// ============================================================
// DETAIL · MODAL — Feature view with floating modal overlay
// ============================================================
function DetailModal({ project, accent }) {
  const feature = shortName(pickName(frontendOf(project)[0], project?.projectTitle || "Premium-Upgrade"), 32);
  return (
    <div className="relative bg-[#05050A]">
      {/* Background = blurred / dimmed Default detail */}
      <div className="opacity-40 pointer-events-none filter blur-[2px]">
        <DetailVariant project={project} accent={accent} />
      </div>
      {/* Dim overlay */}
      <div className="absolute inset-0 bg-black/55" />

      {/* Floating modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md rounded-xl overflow-hidden border shadow-2xl" style={{ background: "#0A0A14", borderColor: `${accent}40` }}>
          {/* Header strip */}
          <div className="px-4 py-2 flex items-center gap-2 border-b border-white/8" style={{ background: `linear-gradient(90deg, ${accent}20, transparent)` }}>
            <div className="w-5 h-5 rounded flex items-center justify-center" style={{ background: accent }}><span className="text-[8px] font-bold text-black">★</span></div>
            <div className="flex-1 text-[9px] font-['Outfit'] text-white">Bestätigung erforderlich</div>
            <span className="text-[12px] text-white/40">×</span>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Aktion</div>
            <div className="text-white font-['Outfit'] font-light text-[16px] leading-tight mt-1">{feature}</div>
            <div className="text-[8.5px] font-['Outfit'] text-white/65 mt-2 leading-relaxed">
              Du bist im Begriff, diese Aktion auszuführen. Bitte prüfe die Details unten und bestätige, um fortzufahren.
            </div>

            {/* Detail rows */}
            <div className="mt-3 rounded-md bg-white/[0.025] border border-white/8 divide-y divide-white/5">
              {[
                { l: "Empfänger", v: "Anna Müller · CH93 0076 ...3456" },
                { l: "Betrag", v: "CHF 1'250.00" },
                { l: "Referenz", v: "Rechnung Nr. 2026-0142" },
                { l: "Ausführung", v: "Sofort · 2-Faktor erforderlich" },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-2 px-2.5 py-1.5">
                  <span className="text-[6.5px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono'] w-20 flex-shrink-0">{r.l}</span>
                  <span className="text-[8px] font-['Outfit'] text-white flex-1 truncate">{r.v}</span>
                </div>
              ))}
            </div>

            {/* Inline 2FA hint */}
            <div className="mt-3 flex items-center gap-2 px-2 py-1.5 rounded-md border" style={{ background: `${accent}08`, borderColor: `${accent}28` }}>
              <span className="text-[10px]" style={{ color: accent }}>⌖</span>
              <span className="text-[7px] font-['Outfit'] text-white/80 flex-1">Code per SMS gesendet an <strong>+41 79 ••• 56 78</strong></span>
              <div className="flex gap-0.5">
                {[1, 4, 8, 2].map((d, i) => (
                  <div key={i} className="w-4 h-5 rounded flex items-center justify-center text-[8px] font-['JetBrains_Mono'] text-white" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}30` }}>{d}</div>
                ))}
                <div className="w-4 h-5 rounded flex items-center justify-center text-[8px] font-['JetBrains_Mono']" style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.15)" }}>_</div>
                <div className="w-4 h-5 rounded flex items-center justify-center text-[8px] font-['JetBrains_Mono']" style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.15)" }}>_</div>
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-4 flex gap-2">
              <div className="flex-1 px-3 py-2 rounded text-center text-[8px] font-['Outfit'] border border-white/10 text-white/70">Abbrechen</div>
              <div className="flex-1 px-3 py-2 rounded text-center text-[8px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Bestätigen →</div>
            </div>

            <div className="mt-2 text-center text-[6px] font-['JetBrains_Mono'] text-white/30">Geschützt durch 2-Faktor · Audit-Log AKTIV</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Detail with sub-tabs (Feature / Flow / Modal) — wraps every detail variant
function DetailMockup({ project, accent }) {
  const [subTab, setSubTab] = useState("feature");
  const subTabs = [
    { k: "feature", l: "Feature" },
    { k: "flow", l: "Flow" },
    { k: "modal", l: "Modal" },
  ];

  return (
    <div className="bg-[#05050A]">
      {/* Sub-tab bar — inside the browser chrome, above the screen */}
      <div className="px-3 py-2 border-b border-white/5 flex items-center gap-2" style={{ background: "rgba(255,255,255,0.015)" }}>
        <span className="text-[6px] tracking-widest uppercase text-white/40 font-['JetBrains_Mono']">Ansicht</span>
        <div className="flex gap-0.5 p-0.5 rounded-md bg-white/[0.04] border border-white/8">
          {subTabs.map((t) => {
            const isActive = subTab === t.k;
            return (
              <button
                key={t.k}
                data-testid={`detail-subtab-${t.k}`}
                onClick={() => setSubTab(t.k)}
                className="px-2.5 py-0.5 rounded text-[10px] font-['Outfit'] transition-all"
                style={isActive
                  ? { color: accent, background: `${accent}18` }
                  : { color: "rgba(255,255,255,0.55)", background: "transparent" }}
              >
                {t.l}
              </button>
            );
          })}
        </div>
        <span className="ml-auto text-[6px] tracking-widest uppercase text-white/30 font-['JetBrains_Mono'] hidden sm:inline">
          {subTab === "feature" && "Hauptansicht"}
          {subTab === "flow" && "3-Schritt-Prozess"}
          {subTab === "modal" && "Overlay-Dialog"}
        </span>
      </div>

      {/* Variant content */}
      {subTab === "feature" && <DetailVariant project={project} accent={accent} />}
      {subTab === "flow" && <DetailFlow project={project} accent={accent} />}
      {subTab === "modal" && <DetailModal project={project} accent={accent} />}
    </div>
  );
}

// ============================================================
// FORM — Multi-step wizard with real input fields
// ============================================================
function FormMockup({ project, accent }) {
  const sections = frontendOf(project);
  const subject = pickName(sections[0], project?.projectTitle || "App");
  const steps = ["Konto", "Profil", "Verifizierung", "Fertig"];
  return (
    <div className="bg-[#05050A]">
      {/* Top bar */}
      <div className="px-5 py-2.5 border-b border-white/5 flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded" style={{ background: accent }} />
          <div className="text-[9px] font-['Outfit'] text-white tracking-tight">{shortName(subject, 22)}</div>
        </div>
        <span className="ml-auto text-[7px] font-['Outfit'] text-white/40">Brauchst du Hilfe? <span style={{ color: accent }}>→ Support</span></span>
      </div>

      {/* Stepper */}
      <div className="px-6 pt-5 pb-3">
        <div className="flex items-center gap-1">
          {steps.map((s, i) => {
            const active = i === 1;
            const done = i < 1;
            return (
              <div key={s} className="flex items-center gap-1 flex-1">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 font-['JetBrains_Mono'] text-[8px] font-bold"
                  style={{
                    background: done || active ? accent : "rgba(255,255,255,0.04)",
                    color: done || active ? "#000" : "rgba(255,255,255,0.4)",
                    border: `1px solid ${done || active ? accent : "rgba(255,255,255,0.1)"}`,
                  }}
                >
                  {done ? "✓" : i + 1}
                </div>
                <div className="flex-1">
                  <div className="text-[7px] font-['Outfit']" style={{ color: active ? "#fff" : done ? accent : "rgba(255,255,255,0.4)" }}>{s}</div>
                  <div className="h-0.5 rounded-full mt-1" style={{ background: done ? accent : active ? `linear-gradient(90deg, ${accent} 50%, rgba(255,255,255,0.08) 50%)` : "rgba(255,255,255,0.08)" }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Form heading */}
      <div className="px-6 pt-3 pb-1">
        <div className="text-[6px] tracking-widest uppercase font-['JetBrains_Mono']" style={{ color: accent }}>Schritt 2 von 4</div>
        <div className="text-white font-['Outfit'] font-light text-[16px] leading-tight mt-0.5">Erzähl uns kurz von dir</div>
        <div className="text-[8px] text-white/50 mt-1 font-['Outfit']">Diese Angaben helfen uns, dein Erlebnis persönlich zu gestalten. Du kannst sie später jederzeit ändern.</div>
      </div>

      {/* Form fields */}
      <div className="px-6 py-3 grid grid-cols-2 gap-3">
        {/* Name */}
        <div>
          <label className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">Vorname *</label>
          <div className="mt-1 px-2.5 py-2 rounded-md bg-[#0A0A14] border text-[9px] font-['Outfit'] text-white" style={{ borderColor: `${accent}50` }}>
            Anna
          </div>
        </div>
        <div>
          <label className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">Nachname *</label>
          <div className="mt-1 px-2.5 py-2 rounded-md bg-[#0A0A14] border border-white/8 text-[9px] font-['Outfit'] text-white">
            Müller
          </div>
        </div>
        {/* Email */}
        <div className="col-span-2">
          <label className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">E-Mail-Adresse *</label>
          <div className="mt-1 px-2.5 py-2 rounded-md bg-[#0A0A14] border flex items-center gap-2" style={{ borderColor: accent }}>
            <span className="text-[9px] font-['Outfit'] text-white flex-1">anna.mueller@example.ch</span>
            <div className="flex items-center gap-1 px-1 py-0.5 rounded" style={{ background: `${accent}20` }}>
              <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke={accent} strokeWidth="3"><path d="M5 13 l4 4 L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: accent }}>Verfügbar</span>
            </div>
          </div>
        </div>
        {/* Password — error state */}
        <div className="col-span-2">
          <label className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">Passwort *</label>
          <div className="mt-1 px-2.5 py-2 rounded-md bg-[#0A0A14] border flex items-center gap-2" style={{ borderColor: "rgba(255,111,111,0.6)" }}>
            <span className="text-[9px] font-['JetBrains_Mono'] text-white tracking-widest flex-1">••••••</span>
            <span className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: "#FF6F6F" }}>👁</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <span className="text-[6.5px] font-['JetBrains_Mono']" style={{ color: "#FF6F6F" }}>⚠</span>
            <span className="text-[6.5px] font-['Outfit']" style={{ color: "#FF6F6F" }}>Passwort muss mindestens 8 Zeichen enthalten</span>
          </div>
          <div className="mt-1 flex items-center gap-1">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex-1 h-1 rounded" style={{ background: i < 1 ? "#FF6F6F" : "rgba(255,255,255,0.08)" }} />
            ))}
            <span className="text-[6px] font-['JetBrains_Mono'] text-white/40">Schwach</span>
          </div>
        </div>
        {/* Country */}
        <div>
          <label className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">Land</label>
          <div className="mt-1 px-2.5 py-2 rounded-md bg-[#0A0A14] border border-white/8 flex items-center justify-between">
            <span className="text-[9px] font-['Outfit'] text-white">🇨🇭 Schweiz</span>
            <span className="text-[8px] text-white/40">▾</span>
          </div>
        </div>
        {/* Date */}
        <div>
          <label className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">Geburtstag</label>
          <div className="mt-1 px-2.5 py-2 rounded-md bg-[#0A0A14] border border-white/8 flex items-center justify-between">
            <span className="text-[9px] font-['JetBrains_Mono'] text-white/40">TT.MM.JJJJ</span>
            <span className="text-[9px] text-white/40">📅</span>
          </div>
        </div>
        {/* Bio textarea */}
        <div className="col-span-2">
          <label className="text-[7px] tracking-widest uppercase text-white/50 font-['JetBrains_Mono']">Kurz-Bio (optional)</label>
          <div className="mt-1 px-2.5 py-2 rounded-md bg-[#0A0A14] border border-white/8 min-h-[44px]">
            <span className="text-[9px] font-['Outfit'] text-white/40 leading-relaxed">Erzähl uns in 1–2 Sätzen, wer du bist…</span>
          </div>
        </div>
        {/* Checkbox */}
        <div className="col-span-2 flex items-start gap-1.5 pt-1">
          <div className="w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center mt-0.5" style={{ background: accent, border: `1px solid ${accent}` }}>
            <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="#000" strokeWidth="3"><path d="M5 13 l4 4 L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <span className="text-[7.5px] font-['Outfit'] text-white/80">Ich akzeptiere die <span style={{ color: accent, textDecoration: "underline" }}>AGB</span> und die <span style={{ color: accent, textDecoration: "underline" }}>Datenschutzerklärung</span>.</span>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="px-6 py-3 border-t border-white/5 flex items-center justify-between gap-2 bg-white/[0.015]">
        <div className="px-3 py-1.5 rounded text-[8px] font-['Outfit'] bg-white/8 text-white/70 border border-white/10">← Zurück</div>
        <div className="flex items-center gap-2">
          <span className="text-[7px] font-['JetBrains_Mono'] text-white/40">Schritt 2 / 4</span>
          <div className="px-4 py-1.5 rounded text-[8px] font-['Outfit'] font-semibold" style={{ background: accent, color: "#000" }}>Weiter →</div>
        </div>
      </div>
    </div>
  );
}

const VARIANTS = { landing: LandingMockup, dashboard: DashboardMockup, detail: DetailMockup, form: FormMockup };
const TAB_KEYS = ["landing", "dashboard", "detail", "form"];

// Mobile frame wrapper — wraps any mockup into a phone-like container
function MobileFrame({ children, accent }) {
  return (
    <div className="flex justify-center bg-[#0A0A14] py-6 px-4">
      <div
        className="relative rounded-[24px] border-[6px] overflow-hidden shadow-2xl"
        style={{ width: 360, borderColor: "#1A1A22", maxWidth: "100%" }}
      >
        {/* notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-3.5 rounded-b-xl bg-[#1A1A22] z-10" />
        <div className="bg-[#05050A]" style={{ minHeight: 640 }}>
          {children}
        </div>
        {/* home indicator */}
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-20 h-1 rounded-full bg-white/30" />
      </div>
    </div>
  );
}

export default function ProjectPreview2D({
  project,
  variant = "landing",
  compact = false,
  showDisclaimer = true,
  className = "",
  labels,
  subtitle,
  disclaimerHtml,
}) {
  const [active, setActive] = useState(variant);
  const [mode, setMode] = useState("desktop"); // "desktop" | "mobile"
  const tabLabels = {
    landing: labels?.tab_landing || DEFAULT_LABELS.landing,
    dashboard: labels?.tab_dashboard || DEFAULT_LABELS.dashboard,
    detail: labels?.tab_detail || DEFAULT_LABELS.detail,
    form: labels?.tab_form || "Form",
  };
  const accent = useMemo(() => {
    const cat = (project?.projectCategory || "").toLowerCase();
    if (cat.includes("fintech") || cat.includes("bank")) return "#3B82F6";
    if (cat.includes("health") || cat.includes("medi")) return "#10B981";
    if (cat.includes("commerce") || cat.includes("retail")) return "#F97316";
    if (cat.includes("ai") || cat.includes("data")) return "#A855F7";
    return "#00F0FF";
  }, [project?.projectCategory]);

  const slug = (project?.projectTitle || "your-app")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30);
  const host = `${slug}.app`;

  const Variant = VARIANTS[active] || LandingMockup;

  if (compact) {
    return (
      <div data-testid="project-preview-2d-compact" className={className}>
        <BrowserChrome accent={accent} host={host}>
          <div className="origin-top-left scale-[0.55] w-[182%] h-[182%] -mb-[82%]">
            <Variant project={project} accent={accent} />
          </div>
        </BrowserChrome>
      </div>
    );
  }

  return (
    <div data-testid="project-preview-2d" className={className}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/8">
          {TAB_KEYS.map((key) => {
            const Icon = TAB_ICONS[key] || FileText;
            const isActive = active === key;
            return (
              <button
                key={key}
                data-testid={`preview-tab-${key}`}
                onClick={() => setActive(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-['Outfit'] transition-all ${
                  isActive ? "bg-white/10 text-white" : "text-[#909098] hover:text-white"
                }`}
                style={isActive ? { color: accent, background: `${accent}15` } : {}}
              >
                <Icon className="w-3.5 h-3.5" />
                {tabLabels[key]}
              </button>
            );
          })}
        </div>
        {/* Mode toggle */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-0.5 p-1 rounded-lg bg-white/[0.03] border border-white/8">
            {[
              { k: "desktop", l: labels?.mode_desktop || "Desktop", icon: "🖥" },
              { k: "mobile", l: labels?.mode_mobile || "Mobile", icon: "📱" },
            ].map((m) => {
              const isActive = mode === m.k;
              return (
                <button
                  key={m.k}
                  data-testid={`preview-mode-${m.k}`}
                  onClick={() => setMode(m.k)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-['Outfit'] transition-all ${
                    isActive ? "bg-white/10 text-white" : "text-[#909098] hover:text-white"
                  }`}
                  style={isActive ? { color: accent, background: `${accent}15` } : {}}
                >
                  <span className="text-[12px] leading-none">{m.icon}</span>
                  {m.l}
                </button>
              );
            })}
          </div>
          {subtitle && (
            <span className="text-[10px] font-['JetBrains_Mono'] tracking-widest uppercase text-[#606070]">
              {subtitle}
            </span>
          )}
        </div>
      </div>
      <BrowserChrome accent={accent} host={host}>
        {mode === "mobile" ? (
          <MobileFrame accent={accent}>
            <Variant project={project} accent={accent} />
          </MobileFrame>
        ) : (
          <Variant project={project} accent={accent} />
        )}
      </BrowserChrome>
      {showDisclaimer && (
        <div className="mt-3 flex items-start gap-2 p-3 rounded-md bg-[#FFB800]/8 border border-[#FFB800]/25">
          <Info className="w-3.5 h-3.5 text-[#FFB800] flex-shrink-0 mt-0.5" />
          <p
            className="text-[11px] text-[#C0C0C8] leading-relaxed [&_strong]:text-white"
            dangerouslySetInnerHTML={{ __html: disclaimerHtml || DEFAULT_DISCLAIMER }}
          />
        </div>
      )}
    </div>
  );
}
